(() => {
  const ROOT_SELECTOR = '.kozler-cart[data-section-id]';

  const initKozlerCartDrawer = (root) => {
    if (!root || root.dataset.kozlerInitialized === 'true') return;
    root.dataset.kozlerInitialized = 'true';
    root.style.display = '';
    const sectionId = root.dataset.sectionId || '';

    const content = root.querySelector('[data-cart-content]');
    const badge = root.querySelector('[data-cart-count]');
    const clearButton = root.querySelector('[data-clear-cart]');
    const shareButton = root.querySelector('[data-cart-share]');
    const shareLabel = shareButton?.querySelector('[data-share-label]');
    const shareDefaultLabel = shareLabel ? shareLabel.textContent : '';
    const shareCartPath = shareButton?.dataset.cartUrl || root.dataset.cartUrl || '/cart';
    const OPEN_ON_SUBMIT_ATTR = 'data-kozler-cart-open-on-submit';
    const KOZLER_CART_MANUAL_HEADER = 'X-Kozler-Cart-Manual';
    let shareLabelTimer;
    let noteTimer;
    let lastOpenOnSubmit = null;
    let lastOpenOnSubmitAt = 0;
    let lastKnownCartProductIds = new Set();
    let lastKnownCartVariantIds = new Set();

    const normalizeCount = (value) => {
      const parsed = parseInt(value, 10);
      return Number.isFinite(parsed) ? parsed : 0;
    };

    const emitCartCount = (count) => {
      const detail = { count: normalizeCount(count) };
      document.dispatchEvent(new CustomEvent('kozler:cart:count', { detail }));
    };

    const dispatchLegacyCartEvents = (cartState) => {
      const detail = { cart: cartState, source: 'kozler-cart-drawer' };
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail, bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart:updated', { detail, bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart:change', { detail, bubbles: true }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { detail, bubbles: true }));
    };

    const syncHeaderCartBadges = (count) => {
      const nextCount = normalizeCount(count);
      document.querySelectorAll('[data-kozler-cart-trigger]').forEach((triggerEl) => {
        const baseLabel =
          triggerEl.getAttribute('data-kozler-cart-label-base') ||
          triggerEl.getAttribute('aria-label') ||
          'Cart';

        triggerEl.setAttribute('aria-label', nextCount > 0 ? `${baseLabel} (${nextCount})` : baseLabel);

        const cartStack = triggerEl.querySelector('.kozler-custom-nav__cart-stack');
        if (!cartStack) {
          let bubbleEl = triggerEl.querySelector('.cart-count-bubble');
          if (!bubbleEl && nextCount > 0 && triggerEl.id === 'cart-icon-bubble') {
            bubbleEl = document.createElement('div');
            bubbleEl.className = 'cart-count-bubble';
            const visibleCount = document.createElement('span');
            visibleCount.setAttribute('aria-hidden', 'true');
            bubbleEl.appendChild(visibleCount);
            triggerEl.appendChild(bubbleEl);
          }

          if (!bubbleEl) return;
          bubbleEl.hidden = nextCount <= 0;
          const visibleCount = bubbleEl.querySelector('[aria-hidden="true"]');
          if (visibleCount) {
            visibleCount.textContent = nextCount > 0 && nextCount < 100 ? String(nextCount) : '';
          }
          return;
        }
        if (triggerEl.getAttribute('data-kozler-cart-show-count') === 'false') return;

        let countEl = triggerEl.querySelector('[data-kozler-cart-count-badge]');
        if (!countEl && nextCount > 0) {
          countEl = document.createElement('span');
          countEl.className = 'kozler-custom-nav__cart-count';
          countEl.setAttribute('aria-hidden', 'true');
          countEl.setAttribute('data-kozler-cart-count-badge', '');
          cartStack.appendChild(countEl);
        }

        if (!countEl) return;
        countEl.textContent = nextCount > 0 ? String(nextCount) : '';
        countEl.hidden = nextCount <= 0;
      });

      const badges = document.querySelectorAll('.cart-qty');
      badges.forEach((badgeEl) => {
        let valueEl = badgeEl.querySelector('[data-kozler-cart-count-value]');
        if (!valueEl) {
          valueEl = document.createElement('span');
          valueEl.setAttribute('data-kozler-cart-count-value', '');
          valueEl.setAttribute('aria-hidden', 'true');
          badgeEl.appendChild(valueEl);
        }
        valueEl.textContent = String(nextCount);
        badgeEl.classList.toggle('opacity-0', nextCount <= 0);
      });

      document.querySelectorAll('cart-link').forEach((cartLinkEl) => {
        cartLinkEl.querySelectorAll('items-count').forEach((countEl) => {
          countEl.textContent = String(nextCount);
        });

        const trigger = cartLinkEl.querySelector(
          '.js-cart-icon, .secondary-nav__item--cart, .header-trigger__link--cart'
        );
        if (trigger) {
          trigger.classList.toggle('js-cart-full', nextCount > 0);
        }
      });
    };

    const syncDrawerCountBadge = (count) => {
      if (!badge) return;
      const nextCount = normalizeCount(count);
      badge.setAttribute('data-count-value', String(nextCount));
      let valueEl = badge.querySelector('[data-cart-count-value]');
      if (!valueEl) {
        valueEl = document.createElement('span');
        valueEl.className = 'kozler-cart__count-value';
        valueEl.setAttribute('data-cart-count-value', '');
        badge.textContent = '';
        badge.appendChild(valueEl);
      }
      valueEl.textContent = String(nextCount);
    };

    const updateHeaderActions = (count) => {
      const nextCount = normalizeCount(count);
      root.setAttribute('data-cart-count', String(nextCount));
      const hasItems = nextCount > 0;
      if (clearButton) {
        clearButton.toggleAttribute('hidden', !hasItems);
      }
      if (shareButton) {
        shareButton.toggleAttribute('hidden', !hasItems);
      }
      syncDrawerCountBadge(nextCount);
      syncHeaderCartBadges(nextCount);
      emitCartCount(nextCount);
    };

    updateHeaderActions(root.dataset.cartCount || 0);

    const getCartAjaxEndpoint = (action = '') => {
      const cartPath = root.dataset.cartUrl || window.routes?.cart_url || '/cart';
      const baseUrl = new URL(cartPath, window.location.origin);
      const basePath = baseUrl.pathname.replace(/\/$/, '') || '/cart';
      const ajaxPath = action ? `${basePath}/${action}.js` : `${basePath}.js`;
      return new URL(ajaxPath, window.location.origin).toString();
    };

    const getCartJsonEndpoint = () => {
      const url = new URL(getCartAjaxEndpoint(), window.location.origin);
      url.searchParams.set('kozler_cache', Date.now().toString());
      return url.toString();
    };

    const fetchNoStore = (url, options = {}) => {
      const headers = new Headers(options.headers || {});
      const method = String(options.method || 'GET').toUpperCase();
      if (method === 'GET' && !headers.has('Cache-Control')) {
        headers.set('Cache-Control', 'no-cache');
      }
      return fetch(url, {
        credentials: 'same-origin',
        cache: 'no-store',
        ...options,
        headers,
      });
    };

    const fetchCartState = () =>
      fetchNoStore(getCartJsonEndpoint()).then((response) => {
        if (!response.ok) {
          throw new Error(`Cart state failed with ${response.status}`);
        }
        return response.json();
      });

    const getSectionEndpoint = (mode = 'section') => {
      const cartPath = root.dataset.cartUrl || window.routes?.cart_url || '/cart';
      const url = new URL(cartPath, window.location.origin);
      if (sectionId) {
        url.searchParams.set(mode === 'sections' ? 'sections' : 'section_id', sectionId);
      }
      url.searchParams.set('kozler_cache', Date.now().toString());
      return url.toString();
    };

    const fetchDrawerSectionHtml = () =>
      fetchNoStore(getSectionEndpoint('section'))
        .then((response) => {
          if (!response.ok) throw new Error(`Section refresh failed with ${response.status}`);
          return response.text();
        })
        .then((html) => {
          if (html && html.includes(`kozler-cart-${sectionId}`)) return html;
          throw new Error('Section refresh returned unexpected markup');
        })
        .catch(() =>
          fetchNoStore(getSectionEndpoint('sections'))
            .then((response) => {
              if (!response.ok) throw new Error(`Sections refresh failed with ${response.status}`);
              return response.json();
            })
            .then((sections) => {
              const html = sections && sections[sectionId];
              if (!html) throw new Error(`Sections refresh did not include ${sectionId}`);
              return html;
            })
        );

    let busyCount = 0;

    const getCheckoutElements = () => ({
      button: root.querySelector('[data-checkout-button]'),
      dynamic: root.querySelector('.kozler-cart__dynamic-checkout'),
    });

    const updateCheckoutState = () => {
      const terms = getTermsElements();
      const termsChecked = terms ? !!terms.checkbox?.checked : true;
      const termsDisabled = terms ? (terms.required && !termsChecked) : false;
      const busy = busyCount > 0;
      const { button, dynamic } = getCheckoutElements();

      if (button) {
        button.toggleAttribute('disabled', busy);
        button.setAttribute('aria-disabled', busy ? 'true' : 'false');
        button.classList.toggle('is-disabled', busy);
      }
      if (dynamic) {
        const disableDynamic = busy || termsDisabled;
        dynamic.classList.toggle('is-disabled', disableDynamic);
        dynamic.toggleAttribute('aria-disabled', disableDynamic);
      }
    };

    const setBusy = (state) => {
      if (state) {
        busyCount += 1;
      } else {
        busyCount = Math.max(0, busyCount - 1);
      }
      root.classList.toggle('is-loading', busyCount > 0);
      updateCheckoutState();
    };

    const startControlLoading = (el) => {
      if (!el) return;
      el.classList.add('is-loading');
      el.setAttribute('aria-busy', 'true');
    };

    const stopControlLoading = (el) => {
      if (!el) return;
      el.classList.remove('is-loading');
      el.removeAttribute('aria-busy');
    };

    const formatMoney = (cents) => {
      const mf = root.dataset.moneyFormat || '';
      const currency = root.dataset.currency || 'USD';
      if (window.Shopify && window.Shopify.formatMoney) {
        try {
          return window.Shopify.formatMoney(cents, mf);
        } catch (e) {
          /* noop */
        }
      }
      try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency, currencyDisplay: 'narrowSymbol' }).format(cents / 100);
      } catch (e) {
        return (cents / 100).toFixed(2);
      }
    };

    const normalizeMoneyCents = (value) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? Math.round(parsed) : 0;
    };

    const stripHtml = (value) => String(value || '').replace(/<[^>]*>/g, '').trim();
    const escapeHtmlAttr = (value) =>
      String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

    const getUpsellPriceMarkup = (priceCents, compareAtCents) => {
      const normalizedPrice = normalizeMoneyCents(priceCents);
      const normalizedCompare = normalizeMoneyCents(compareAtCents);
      const compareMarkup = normalizedCompare > normalizedPrice
        ? `<span class="kozler-cart-item__price--compare">${formatMoney(normalizedCompare)}</span>`
        : '';
      return `${compareMarkup}<span class="kozler-cart-item__price--current">${formatMoney(normalizedPrice)}</span>`;
    };

    const getUpsellAddButtonLabel = (baseLabel, priceCents, showPrice) => {
      const label = String(baseLabel || 'Add');
      if (!showPrice) return label;
      const normalizedPrice = normalizeMoneyCents(priceCents);
      return `${label} - ${stripHtml(formatMoney(normalizedPrice))}`;
    };

    const getUpsellControlStyle = (list) => {
      const style = (list?.dataset?.upsellControlStyle || 'button').toLowerCase();
      if (style === 'checkbox' || style === 'switch') return style;
      return 'button';
    };

    const shouldHideUpsellWhenAdded = (list) => list?.dataset?.hideWhenAdded === 'true';
    const shouldShowUpsellAddedState = (list) => list?.dataset?.showAddedState === 'true';
    const shouldUpsellLabelSwitchToAddedState = (list) => list?.dataset?.upsellLabelSwitchesAddedState !== 'false';

    const getUpsellAddedLabel = (list, labelEl) => {
      const fromLabel = labelEl?.getAttribute('data-added-label');
      const fromList = list?.dataset?.addedLabel;
      return String(fromLabel || fromList || 'Added');
    };

    const syncUpsellPrice = (upsellItem, priceCents, compareAtCents) => {
      const priceEl = upsellItem?.querySelector('[data-upsell-price]');
      if (!priceEl) return;
      priceEl.innerHTML = getUpsellPriceMarkup(priceCents, compareAtCents);
    };

    const parseEmbeddedJson = (node, fallback = null) => {
      if (!node) return fallback;
      try {
        return JSON.parse(node.textContent || '');
      } catch (error) {
        return fallback;
      }
    };

    const normalizeOneClickMatcher = (value) => String(value || '').trim().toLowerCase();

    const parseOneClickThresholdCents = (value) => {
      const cleaned = String(value || '')
        .replace(/[^\d.,-]/g, '')
        .replace(/,/g, '')
        .trim();
      if (!cleaned) return null;
      const parsed = Number.parseFloat(cleaned);
      if (!Number.isFinite(parsed)) return null;
      return Math.max(0, Math.round(parsed * 100));
    };

    const parseOneClickRules = (rawText) =>
      String(rawText || '')
        .split(/\r?\n|;|\|/)
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => {
          const parts = line.split(/\s*(?:=>|=|:)\s*/);
          if (parts.length < 2) return null;
          const thresholdCents = parseOneClickThresholdCents(parts.shift());
          const variantRef = parts.join('=').trim();
          if (!Number.isFinite(thresholdCents) || !variantRef) return null;
          return { thresholdCents, variantRef };
        })
        .filter(Boolean)
        .sort((a, b) => a.thresholdCents - b.thresholdCents);

    const findOneClickVariantByReference = (variants, reference) => {
      const normalizedReference = normalizeOneClickMatcher(reference);
      if (!normalizedReference) return null;

      const byId = variants.find((variant) => String(variant.id) === normalizedReference);
      if (byId) return byId;

      return (
        variants.find((variant) => normalizeOneClickMatcher(variant.title) === normalizedReference) ||
        variants.find((variant) => normalizeOneClickMatcher(variant.sku) === normalizedReference) ||
        null
      );
    };

    const getOneClickConfig = (upsellEl) => {
      if (!upsellEl) return null;
      if (upsellEl._kozlerOneClickConfig) return upsellEl._kozlerOneClickConfig;

      const configData = parseEmbeddedJson(upsellEl.querySelector('[data-oneclick-config]'), null);
      if (!configData || !Array.isArray(configData.variants) || !configData.variants.length) return null;

      const variants = configData.variants.map((variant) => ({
        id: String(variant.id),
        title: String(variant.title || ''),
        available: !!variant.available,
        price: normalizeMoneyCents(variant.price),
        compareAtPrice: normalizeMoneyCents(variant.compareAtPrice),
        sku: String(variant.sku || ''),
      }));
      const defaultVariant =
        variants.find((variant) => variant.id === String(configData.defaultVariantId)) ||
        variants.find((variant) => variant.available) ||
        variants[0];
      const rules = parseOneClickRules(configData.rulesText).map((rule) => ({
        thresholdCents: rule.thresholdCents,
        variant: findOneClickVariantByReference(variants, rule.variantRef),
      })).filter((rule) => rule.variant);

      const config = {
        defaultVariant,
        dynamicEnabled: !!configData.dynamicEnabled,
        excludeSelfFromSubtotal: configData.excludeSelfFromSubtotal !== false,
        productId: String(configData.productId),
        rules,
        variants,
      };

      upsellEl._kozlerOneClickConfig = config;
      return config;
    };

    const getOneClickCartLines = (cartState, config) => {
      if (!cartState || !Array.isArray(cartState.items) || !config) return [];
      return cartState.items.filter((item) => String(item.product_id) === config.productId);
    };

    const getOneClickThresholdSubtotal = (cartState, config) => {
      if (!cartState || !Array.isArray(cartState.items)) return 0;
      return cartState.items.reduce((sum, item) => {
        if (config.excludeSelfFromSubtotal && String(item.product_id) === config.productId) {
          return sum;
        }
        return sum + normalizeMoneyCents(item.final_line_price);
      }, 0);
    };

    const resolveOneClickVariant = (config, cartState) => {
      if (!config) return null;

      const cartLines = getOneClickCartLines(cartState, config);
      const currentVariant =
        cartLines
          .map((line) => config.variants.find((variant) => variant.id === String(line.variant_id)))
          .find(Boolean) || null;

      if (!config.dynamicEnabled || !config.rules.length) {
        return currentVariant || config.defaultVariant;
      }

      const subtotal = getOneClickThresholdSubtotal(cartState, config);
      let chosenVariant = config.defaultVariant;
      config.rules.forEach((rule) => {
        if (subtotal >= rule.thresholdCents) {
          chosenVariant = rule.variant;
        }
      });

      if (chosenVariant?.available) return chosenVariant;
      return config.variants.find((variant) => variant.available) || config.defaultVariant;
    };

    const syncOneClickUpsellUi = (upsellEl, cartState) => {
      const config = getOneClickConfig(upsellEl);
      if (!config) return null;

      const variant = resolveOneClickVariant(config, cartState);
      if (!variant) return config;

      const cartLines = getOneClickCartLines(cartState, config);
      const hasAnyVariantInCart = cartLines.length > 0;
      const priceEl = upsellEl.querySelector('[data-oneclick-price]');
      const toggle = upsellEl.querySelector('[data-oneclick-toggle]');

      upsellEl.setAttribute('data-variant-id', variant.id);
      upsellEl.setAttribute('data-in-cart', hasAnyVariantInCart ? 'true' : 'false');

      if (priceEl) {
        priceEl.innerHTML = getUpsellPriceMarkup(variant.price, variant.compareAtPrice);
      }

      if (toggle) {
        toggle.checked = hasAnyVariantInCart;
        toggle.disabled = !variant.available && !hasAnyVariantInCart;
      }

      return config;
    };

    const postVariantUpdates = (updates) => {
      const normalizedUpdates = Object.entries(updates || {}).reduce((acc, [variantId, quantity]) => {
        if (!variantId) return acc;
        const normalizedQuantity = Math.max(0, parseInt(quantity, 10) || 0);
        acc[String(variantId)] = normalizedQuantity;
        return acc;
      }, {});

      if (!Object.keys(normalizedUpdates).length) {
        return Promise.resolve();
      }

      return fetchNoStore(getCartAjaxEndpoint('update'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', [KOZLER_CART_MANUAL_HEADER]: 'drawer' },
        body: JSON.stringify({ updates: normalizedUpdates }),
      });
    };

    const removeOneClickProduct = (upsellEl, controlEl) => {
      const config = getOneClickConfig(upsellEl);
      if (!config) {
        const variantId = upsellEl?.getAttribute('data-variant-id');
        return removeVariant(Number(variantId), controlEl);
      }

      const updates = config.variants.reduce((acc, variant) => {
        acc[variant.id] = 0;
        return acc;
      }, {});

      startControlLoading(controlEl);
      setBusy(true);
      return postVariantUpdates(updates)
        .then((cartState) => refreshDrawerAfterMutation(cartState))
        .catch((error) => {
          console.error('One-click removal failed', error);
          stopControlLoading(controlEl);
        })
        .finally(() => {
          setBusy(false);
        });
    };

    const syncOneClickUpsells = (cartState) => {
      const upsellEls = Array.from(root.querySelectorAll('[data-oneclick-upsell]'));
      if (!upsellEls.length || !cartState || !Array.isArray(cartState.items)) {
        return Promise.resolve({ mutated: false });
      }

      const processedProductIds = new Set();
      const updates = {};
      let shouldMutate = false;

      upsellEls.forEach((upsellEl) => {
        const config = syncOneClickUpsellUi(upsellEl, cartState);
        if (!config || !config.dynamicEnabled || processedProductIds.has(config.productId)) return;
        processedProductIds.add(config.productId);

        const targetVariant = resolveOneClickVariant(config, cartState);
        if (!targetVariant) return;

        const cartLines = getOneClickCartLines(cartState, config);
        if (!cartLines.length) return;

        const activeTargetQty = cartLines
          .filter((line) => String(line.variant_id) === targetVariant.id)
          .reduce((sum, line) => sum + (parseInt(line.quantity, 10) || 0), 0);
        const activeOtherQty = cartLines
          .filter((line) => String(line.variant_id) !== targetVariant.id)
          .reduce((sum, line) => sum + (parseInt(line.quantity, 10) || 0), 0);

        if (activeTargetQty === 1 && activeOtherQty === 0) return;

        config.variants.forEach((variant) => {
          updates[variant.id] = 0;
        });
        updates[targetVariant.id] = 1;
        shouldMutate = true;
      });

      if (!shouldMutate) {
        return Promise.resolve({ mutated: false });
      }

      return postVariantUpdates(updates)
        .then(() => refreshDrawerAfterNestedMutation())
        .then(() => ({ mutated: true }))
        .catch((error) => {
          console.error('One-click rule sync failed', error);
          return { mutated: false, error };
        });
    };

    const setShareLabel = (text, autoReset = false) => {
      if (!shareLabel) return;
      shareLabel.textContent = text;
      if (autoReset && shareDefaultLabel) {
        clearTimeout(shareLabelTimer);
        shareLabelTimer = setTimeout(() => {
          shareLabel.textContent = shareDefaultLabel;
        }, 1600);
      }
    };

    const buildCartShareUrl = (cartState) => {
      const items = cartState && Array.isArray(cartState.items) ? cartState.items : [];
      if (!items.length) return null;
      const parts = items
        .map((item) => {
          if (!item || !item.variant_id || !item.quantity) return null;
          return `${item.variant_id}:${item.quantity}`;
        })
        .filter(Boolean);
      if (!parts.length) return null;
      const cartBase = shareCartPath && shareCartPath !== '' ? shareCartPath : '/cart';
      const cleanedCartBase = cartBase.replace(/\/$/, '');
      const basePath = `${cleanedCartBase}/${parts.join(',')}`;

      return `${window.location.origin}${basePath}/checkout`;
    };

    const copyCartLink = (url) => {
      if (!url) return Promise.reject(new Error('Missing cart link'));
      const fallbackCopy = () => new Promise((resolve, reject) => {
        try {
          const textarea = document.createElement('textarea');
          textarea.value = url;
          textarea.setAttribute('readonly', '');
          textarea.style.position = 'absolute';
          textarea.style.left = '-9999px';
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand('copy');
          textarea.remove();
          resolve();
        } catch (error) {
          reject(error);
        }
      });

      if (navigator.clipboard && navigator.clipboard.writeText) {
        return navigator.clipboard.writeText(url).catch(() => fallbackCopy());
      }
      return fallbackCopy();
    };

    const validateNoteRequirement = () => {
      const noteBlock = content.querySelector('[data-note-block]');
      if (!noteBlock) return true;
      const required = noteBlock.getAttribute('data-note-required') === 'true';
      if (!required) return true;
      const textarea = noteBlock.querySelector('[data-cart-note]');
      const error = noteBlock.querySelector('[data-note-error]');
      if (!textarea) return true;
      const value = (textarea.value || '').trim();
      if (value.length === 0) {
        if (error) error.hidden = false;
        noteBlock.classList.add('has-error');
        const body = noteBlock.querySelector('.kozler-cart__note-body');
        const toggle = noteBlock.querySelector('[data-note-toggle]');
        if (body && body.hasAttribute('hidden')) {
          body.removeAttribute('hidden');
          if (toggle) toggle.setAttribute('aria-expanded', 'true');
        }
        textarea.focus();
        return false;
      }
      if (error) error.hidden = true;
      noteBlock.classList.remove('has-error');
      return true;
    };

    const getTermsElements = () => {
      const block = content.querySelector('[data-terms-block]');
      if (!block) return null;
      return {
        block,
        required: block.getAttribute('data-terms-required') === 'true',
        checkbox: block.querySelector('[data-terms-checkbox]'),
        error: block.querySelector('[data-terms-error]'),
        dynamicCheckout: content.querySelector('.kozler-cart__dynamic-checkout'),
      };
    };

    const updateTermsState = () => {
      const terms = getTermsElements();
      if (!terms) {
        updateCheckoutState();
        return;
      }
      const checked = !!terms.checkbox?.checked;
      const shouldDisable = terms.required && !checked;
      if (terms.checkbox) {
        terms.checkbox.setAttribute('aria-invalid', shouldDisable ? 'true' : 'false');
      }
      updateCheckoutState();
    };

    const validateTermsRequirement = () => {
      const terms = getTermsElements();
      if (!terms || !terms.required) return true;
      const checked = !!terms.checkbox?.checked;
      if (!checked) {
        if (terms.error) terms.error.hidden = false;
        terms.block.classList.add('has-error');
        updateTermsState();
        terms.checkbox?.focus();
        return false;
      }
      if (terms.error) terms.error.hidden = true;
      terms.block.classList.remove('has-error');
      updateTermsState();
      return true;
    };

    const shareCart = () => {
      if (!shareButton) return;
      startControlLoading(shareButton);
      shareButton.disabled = true;
      return fetchCartState()
        .then((cartState) => {
          const shareUrl = buildCartShareUrl(cartState);
          if (!shareUrl) {
            setShareLabel('Empty', true);
            throw new Error('Cart is empty');
          }
          return copyCartLink(shareUrl).then(() => {
            setShareLabel('Copied!', true);
          });
        })
        .catch((error) => {
          console.error('Cart share failed', error);
          setShareLabel('Error', true);
        })
        .finally(() => {
          stopControlLoading(shareButton);
          shareButton.disabled = false;
        });
    };

    const clearCart = () => {
      if (!clearButton) return;
      startControlLoading(clearButton);
      clearButton.disabled = true;
      setBusy(true);
      return fetchNoStore(getCartAjaxEndpoint('clear'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', [KOZLER_CART_MANUAL_HEADER]: 'drawer' },
      })
        .then(() => refreshDrawerAfterMutation())
        .catch((error) => {
          console.error('Cart clear failed', error);
        })
        .finally(() => {
          stopControlLoading(clearButton);
          clearButton.disabled = false;
          setBusy(false);
        });
    };

    const AUTO_ADD_SUPPRESS_ATTR = 'kozler_auto_add_suppressed';
    const autoAddSuppressed = new Set();
    const smartRecoCache = new Map();

    const collectAutoAddRules = () => {
      const rules = [];
      content.querySelectorAll('[data-auto-add-rule]').forEach((el) => {
        const trigger = el.getAttribute('data-trigger-variant');
        const target = el.getAttribute('data-target-variant');
        const qty = parseInt(el.getAttribute('data-target-qty') || '1', 10) || 1;
        if (trigger && target) {
          const ruleId = el.getAttribute('data-rule-id') || `${trigger}:${target}`;
          rules.push({ id: ruleId, trigger: String(trigger), target: String(target), qty });
        }
      });
      return rules;
    };

    const parseSuppressedRules = (value) => {
      if (!value) return [];
      if (Array.isArray(value)) return value.map(String).filter(Boolean);
      return String(value)
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean);
    };

    const loadSuppressedRules = (cartState) => {
      const attrValue = cartState && cartState.attributes ? cartState.attributes[AUTO_ADD_SUPPRESS_ATTR] : '';
      parseSuppressedRules(attrValue).forEach((id) => autoAddSuppressed.add(id));
    };

    const updateCartAttributes = (attributes) => {
      setBusy(true);
      return fetchNoStore(getCartAjaxEndpoint('update'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', [KOZLER_CART_MANUAL_HEADER]: 'drawer' },
        body: JSON.stringify({ attributes }),
      })
        .catch((error) => {
          console.error('Cart attribute update failed', error);
        })
        .finally(() => {
          setBusy(false);
        });
    };

    const setSuppressedRules = (ruleIds) => {
      ruleIds.forEach((id) => autoAddSuppressed.add(String(id)));
      const nextValue = Array.from(autoAddSuppressed).join(',');
      return updateCartAttributes({ [AUTO_ADD_SUPPRESS_ATTR]: nextValue });
    };

    const suppressAutoAddForVariant = (variantId) => {
      const rules = collectAutoAddRules();
      const targets = rules.filter((rule) => rule.target === String(variantId)).map((rule) => rule.id);
      if (!targets.length) return;
      setSuppressedRules(targets);
    };

    const parseIdCsv = (value) =>
      String(value || '')
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean);

    const setLastKnownCartProductIds = (cartState) => {
      const cartItems = cartState && Array.isArray(cartState.items) ? cartState.items : [];
      lastKnownCartProductIds = new Set(
        cartItems
          .map((item) => String(item && item.product_id ? item.product_id : ''))
          .filter(Boolean)
      );
      lastKnownCartVariantIds = new Set(
        cartItems
          .map((item) => String(item && item.variant_id ? item.variant_id : ''))
          .filter(Boolean)
      );
    };

    const getUpsellAddedStorageKey = (list) => {
      const blockId = list?.dataset?.upsellBlockId;
      if (!blockId || !sectionId) return null;
      return `kozler_upsell_added:${sectionId}:${blockId}`;
    };

    const getStoredUpsellProductIds = (list) => {
      const key = getUpsellAddedStorageKey(list);
      if (!key) return [];
      try {
        return parseIdCsv(window.sessionStorage.getItem(key));
      } catch (error) {
        return [];
      }
    };

    const setStoredUpsellProductIds = (list, productIds) => {
      const key = getUpsellAddedStorageKey(list);
      if (!key) return;
      try {
        const normalized = Array.from(new Set(parseIdCsv((productIds || []).join(','))));
        if (!normalized.length) {
          window.sessionStorage.removeItem(key);
          return;
        }
        window.sessionStorage.setItem(key, normalized.join(','));
      } catch (error) {
        /* noop */
      }
    };

    const rememberUpsellProductId = (list, productId) => {
      const key = getUpsellAddedStorageKey(list);
      if (!key || !productId) return;
      try {
        const existingIds = getStoredUpsellProductIds(list);
        const normalizedId = String(productId);
        if (existingIds.includes(normalizedId)) return;
        existingIds.push(normalizedId);
        window.sessionStorage.setItem(key, existingIds.join(','));
      } catch (error) {
        /* noop */
      }
    };

    const forgetUpsellProductId = (list, productId) => {
      if (!list || !productId) return;
      const normalized = String(productId);
      const nextIds = getStoredUpsellProductIds(list).filter((id) => String(id) !== normalized);
      setStoredUpsellProductIds(list, nextIds);
    };

    const syncUpsellToggleLabel = (upsellItem, upsellList, selectedVariantInCart) => {
      if (!upsellItem || !upsellList) return;
      const toggleLabel = upsellItem.querySelector('[data-upsell-toggle-label]');
      const toggleWrap = upsellItem.querySelector('[data-upsell-toggle-wrap]');
      if (!toggleLabel) return;
      const showAddedState = shouldShowUpsellAddedState(upsellList);
      const swapLabelWhenAdded = shouldUpsellLabelSwitchToAddedState(upsellList);
      const addedLabel = getUpsellAddedLabel(upsellList, toggleLabel);
      const addLabel = toggleLabel.getAttribute('data-add-label') || toggleLabel.getAttribute('data-base-label') || upsellList.dataset.buttonLabel || 'Add';
      const useAddedLabel = showAddedState && selectedVariantInCart && swapLabelWhenAdded;
      toggleLabel.textContent = useAddedLabel ? addedLabel : addLabel;
      if (toggleWrap) {
        toggleWrap.classList.toggle('is-added', useAddedLabel);
      }
    };

    const syncUpsellItemState = (upsellItem, cartProductIds = lastKnownCartProductIds, cartVariantIds = lastKnownCartVariantIds) => {
      if (!upsellItem) return;
      const upsellList = upsellItem.closest('.kozler-cart-upsell__list');
      if (!upsellList) return;

      const productId = String(upsellItem.getAttribute('data-upsell-product-id') || '');
      const productInCart = productId !== '' && cartProductIds.has(productId);
      const hideWhenAdded = shouldHideUpsellWhenAdded(upsellList);
      const controlStyle = getUpsellControlStyle(upsellList);

      if (hideWhenAdded && productInCart) {
        upsellItem.hidden = true;
        upsellItem.style.display = 'none';
      } else {
        upsellItem.hidden = false;
        upsellItem.style.display = '';
      }

      if (controlStyle === 'button') return;

      const toggle = upsellItem.querySelector('[data-upsell-toggle]');
      if (!toggle) return;
      const variantSelect = upsellItem.querySelector('[data-upsell-variant-select]');
      const variantId = String(
        (variantSelect && variantSelect.value)
          || toggle.getAttribute('data-variant-id')
          || ''
      );
      if (variantId) {
        toggle.setAttribute('data-variant-id', variantId);
      }
      const selectedVariantInCart = variantId !== '' && cartVariantIds.has(variantId);
      toggle.checked = shouldShowUpsellAddedState(upsellList) && selectedVariantInCart;
      upsellItem.setAttribute('data-upsell-selected-in-cart', selectedVariantInCart ? 'true' : 'false');
      syncUpsellToggleLabel(upsellItem, upsellList, selectedVariantInCart);
    };

    const syncRecommendedUpsellItems = (cartState) => {
      if (cartState && Array.isArray(cartState.items)) {
        setLastKnownCartProductIds(cartState);
      }
      const cartProductIds = lastKnownCartProductIds;
      const cartVariantIds = lastKnownCartVariantIds;
      root.querySelectorAll('[data-upsell-item]').forEach((item) => {
        syncUpsellItemState(item, cartProductIds, cartVariantIds);
      });
    };

    const syncUpsellHeaderForList = (list, cartProductIds, extraProductIds = []) => {
      if (!list) return;
      const upsellContainer = list.closest('.kozler-cart-upsell');
      const upsellHeader = upsellContainer?.querySelector('[data-upsell-header]');
      if (!upsellHeader) return;

      const hideWhenInCart = list.dataset.hideHeaderWhenProductInCart === 'true';
      if (!hideWhenInCart) {
        upsellHeader.hidden = false;
        upsellHeader.setAttribute('aria-hidden', 'false');
        upsellHeader.style.display = '';
        return;
      }

      let productIds = parseIdCsv(list.dataset.upsellProductIds);
      const renderedProductIds = Array.from(list.querySelectorAll('[data-upsell-product-id]'))
        .map((el) => el.getAttribute('data-upsell-product-id'))
        .filter(Boolean)
        .map((id) => String(id));
      const renderedSet = new Set(renderedProductIds);

      if (extraProductIds.length) {
        productIds = productIds.concat(extraProductIds.map((id) => String(id)));
      }
      const rememberedIds = getStoredUpsellProductIds(list).map((id) => String(id));
      const rememberedSet = new Set(rememberedIds);

      if (!productIds.length) {
        productIds = renderedProductIds.slice();
      }
      if (rememberedIds.length) {
        productIds = productIds.concat(rememberedIds);
      }

      const shouldHide = productIds.some((id) => {
        const normalized = String(id);
        const isRendered = renderedSet.has(normalized);
        const isInCart = cartProductIds.has(normalized);
        const isRemembered = rememberedSet.has(normalized);
        return !isRendered && (isInCart || isRemembered);
      });
      upsellHeader.hidden = shouldHide;
      upsellHeader.setAttribute('aria-hidden', shouldHide ? 'true' : 'false');
      upsellHeader.style.display = shouldHide ? 'none' : '';
    };

    const syncRememberedUpsellHeaders = () => {
      root.querySelectorAll('.kozler-cart-upsell__list').forEach((list) => {
        syncUpsellHeaderForList(list, lastKnownCartProductIds);
      });
      syncRecommendedUpsellItems();
    };

    const syncRecommendedUpsellHeaders = (cartState) => {
      const cartItems = cartState && Array.isArray(cartState.items) ? cartState.items : [];
      const cartProductIds = new Set(cartItems.map((item) => String(item.product_id)));
      setLastKnownCartProductIds(cartState);
      root.querySelectorAll('.kozler-cart-upsell__list').forEach((list) => {
        if (shouldHideUpsellWhenAdded(list)) {
          const remembered = getStoredUpsellProductIds(list).map((id) => String(id));
          if (remembered.length) {
            const activeRemembered = remembered.filter((id) => cartProductIds.has(id));
            if (activeRemembered.length !== remembered.length) {
              setStoredUpsellProductIds(list, activeRemembered);
            }
          }
        } else {
          setStoredUpsellProductIds(list, []);
        }
        syncUpsellHeaderForList(list, cartProductIds);
      });
      syncRecommendedUpsellItems(cartState);
    };

    const getCachedProducts = (key, fetcher, ttlMs = 120000) => {
      const cached = smartRecoCache.get(key);
      const now = Date.now();
      if (cached && now - cached.at < ttlMs) {
        return Promise.resolve(cached.products);
      }
      return fetcher().then((products) => {
        const normalized = Array.isArray(products) ? products.filter(Boolean) : [];
        smartRecoCache.set(key, { at: now, products: normalized });
        return normalized;
      });
    };

    const renderSmartUpsells = (cartState) => {
      const lists = root.querySelectorAll('[data-smart-recos="true"]');
      if (!lists.length || !cartState || !cartState.items) return;

      const cartProductIds = new Set(cartState.items.map((item) => String(item.product_id)));

      lists.forEach((list) => {
        const cardStyle = list.dataset.upsellCardStyle || 'compact';
        const controlStyle = getUpsellControlStyle(list);
        const labelSwitchesToAddedState = shouldUpsellLabelSwitchToAddedState(list);
        const limit = parseInt(list.dataset.limit || '4', 10);
        const hideQty = list.dataset.hideQty === 'true';
        const buttonLabel = list.dataset.buttonLabel || 'Add';
        const buttonShowPriceInLabel = list.dataset.buttonShowPriceInLabel === 'true';
        const hideWhenAdded = shouldHideUpsellWhenAdded(list);
        const showAddedState = shouldShowUpsellAddedState(list);
        const addedLabel = list.dataset.addedLabel || 'Added';
        const fallbackHandle = list.dataset.fallbackHandle;
        const imageSize = parseInt(list.dataset.imageSize || '120', 10);
        const imageSizeTraditional = parseInt(list.dataset.imageSizeTraditional || '800', 10);
        const imageWidth = cardStyle === 'traditional' ? Math.max(imageSizeTraditional, imageSize) : imageSize;
        const showVariantPrices = list.dataset.showVariantPrices === 'true';
        const seedIdAttr = list.dataset.seedProductId;
        const seedId = seedIdAttr && seedIdAttr !== '' ? seedIdAttr : (cartState.items[0]?.product_id);

        if (!seedId) {
          list.innerHTML = '';
          return;
        }

        const cartVariantIds = cartState.items.map((item) => item.variant_id);
        syncUpsellHeaderForList(list, cartProductIds);

        const pickVariant = (product) => {
          if (showAddedState && !hideWhenAdded) {
            const variantInCart = product.variants.find((v) => cartVariantIds.includes(v.id));
            if (variantInCart) return variantInCart;
          }
          let chosen = null;
          product.variants.forEach((v) => {
            if (chosen) return;
            const inCart = cartVariantIds.includes(v.id);
            if (v.available && !inCart) chosen = v;
          });
          if (!chosen) chosen = product.variants.find((v) => v.available) || product.variants[0];
          return chosen;
        };

        const buildCard = (product) => {
          const productInCart = cartProductIds.has(String(product.id));
          if (hideWhenAdded && productInCart) return '';
          const variant = pickVariant(product);
          if (!variant) return '';
          const variantInCart = cartVariantIds.includes(variant.id);

          const options = product.variants.map((v) => {
            const vInCart = cartVariantIds.includes(v.id);
            const shouldDisableInCartVariant = controlStyle === 'button' || !showAddedState || hideWhenAdded;
            const disabled = (!v.available || (vInCart && shouldDisableInCartVariant)) ? 'disabled' : '';
            const selected = v.id === variant.id ? 'selected' : '';
            const pricePart = showVariantPrices ? ` — ${formatMoney(v.price)}` : '';
            return `<option value="${v.id}" data-price="${normalizeMoneyCents(v.price)}" data-compare-at-price="${normalizeMoneyCents(v.compare_at_price)}" ${selected} ${disabled}>${v.title}${pricePart}</option>`;
          }).join('');

          const qty = hideQty ? '' : `
            <div class="kozler-cart-item__controls kozler-cart-item__controls--inline">
              <div class="kozler-cart-quantity" data-upsell-qty>
                <button type="button" class="kozler-cart-quantity__btn" data-upsell-qty-button data-direction="down" aria-label="Decrease quantity">&minus;</button>
                <input class="kozler-cart-quantity__input" type="number" min="1" step="1" data-upsell-qty-input value="1" inputmode="numeric">
                <button type="button" class="kozler-cart-quantity__btn" data-upsell-qty-button data-direction="up" aria-label="Increase quantity">+</button>
              </div>
            </div>
          `;

          const imgSrc = (product.featured_image && product.featured_image.src) ? `${product.featured_image.src}&width=${imageWidth}` : (product.images && product.images.length ? `${product.images[0]}&width=${imageWidth}` : null);
          const actionLabel = getUpsellAddButtonLabel(buttonLabel, variant.price, buttonShowPriceInLabel);

          let actionMarkup = '';
          if (controlStyle === 'button') {
            if (!variant.available) {
              actionMarkup = '<span class="kozler-free-shipping__gift-lock">Sold out</span>';
            } else if (variantInCart && showAddedState) {
              actionMarkup = `<span class="kozler-cart-upsell__added">${escapeHtmlAttr(addedLabel)}</span>`;
            } else if (variantInCart) {
              actionMarkup = '<span class="kozler-free-shipping__gift-lock">Sold out</span>';
            } else {
              actionMarkup = `<button type="button" class="kozler-cart-upsell__add" data-upsell-add data-variant-id="${variant.id}" data-base-label="${escapeHtmlAttr(buttonLabel)}">${actionLabel}</button>`;
            }
          } else if (!variant.available) {
            actionMarkup = '<span class="kozler-free-shipping__gift-lock">Sold out</span>';
          } else {
            const checked = showAddedState && variantInCart;
            const checkedAttr = checked ? 'checked' : '';
            const useAddedLabel = checked && labelSwitchesToAddedState;
            const toggleLabel = useAddedLabel ? escapeHtmlAttr(addedLabel) : actionLabel;
            actionMarkup = `
              <div class="kozler-cart-upsell__toggle-row${useAddedLabel ? ' is-added' : ''}" data-upsell-toggle-wrap>
                <span class="kozler-cart-upsell__toggle-label" data-upsell-toggle-label data-base-label="${escapeHtmlAttr(buttonLabel)}" data-add-label="${escapeHtmlAttr(actionLabel)}" data-added-label="${escapeHtmlAttr(addedLabel)}">${toggleLabel}</span>
                <label class="kozler-oneclick__control kozler-oneclick__control--${controlStyle}">
                  <input type="checkbox" class="kozler-oneclick__input" data-upsell-toggle data-variant-id="${variant.id}" data-base-label="${escapeHtmlAttr(buttonLabel)}" aria-label="${escapeHtmlAttr(product.title)}" ${checkedAttr}>
                  <span class="kozler-oneclick__control-ui"></span>
                </label>
              </div>
            `;
          }

          return `
            <article class="kozler-cart-upsell__item" data-upsell-item data-upsell-product-id="${product.id}" data-upsell-product-in-cart="${productInCart ? 'true' : 'false'}" data-upsell-selected-in-cart="${variantInCart ? 'true' : 'false'}">
              <a href="${product.url}" class="kozler-cart-upsell__image">
                ${imgSrc ? `<img src="${imgSrc}" alt="${product.featured_image && product.featured_image.alt ? product.featured_image.alt : product.title}" class="kozler-cart-upsell__img" loading="lazy">` : '<div class="kozler-cart-upsell__placeholder" aria-hidden="true"></div>'}
              </a>
            <div class="kozler-cart-upsell__body">
              <p class="kozler-cart-upsell__title">${product.title}</p>
              <p class="kozler-cart-upsell__price" data-upsell-price>${getUpsellPriceMarkup(variant.price, variant.compare_at_price)}</p>
              <div class="kozler-cart-item__variant">
                ${product.variants.length > 1 ? `
                  <div class="kozler-cart-item__select-wrap">
                    <select class="kozler-cart-item__select" data-upsell-variant-select aria-label="Variant">
                      ${options}
                    </select>
                    <span class="kozler-cart-item__select-spinner" aria-hidden="true"></span>
                  </div>
                ` : ``}
                ${qty}
              </div>
            </div>
            <div class="kozler-cart-upsell__action">
              ${actionMarkup}
            </div>
          </article>
        `;
        };

        const renderList = (products) => {
          if (!products || !products.length) return false;
          const items = products.map(buildCard).filter(Boolean).slice(0, limit).join('');
          if (!items.length) return false;
          list.innerHTML = items;
          const renderedProductIds = Array.from(list.querySelectorAll('[data-upsell-product-id]'))
            .map((node) => node.getAttribute('data-upsell-product-id'))
            .filter(Boolean);
          syncUpsellHeaderForList(list, cartProductIds, renderedProductIds);
          syncRecommendedUpsellItems(cartState);
          return true;
        };

        const fetchRecommendations = (pid) =>
          getCachedProducts(
            `reco:${pid}:${limit}`,
            () =>
              fetch(`/recommendations/products.json?product_id=${pid}&limit=${limit}`)
                .then((res) => res.ok ? res.json() : null)
                .then((data) => data && data.products ? data.products : [])
          );

        const fetchCollection = (handle) =>
          getCachedProducts(
            `collection:${handle}:${limit}`,
            () =>
              fetch(`/collections/${handle}/products.json?limit=${limit}`)
                .then((res) => res.ok ? res.json() : null)
                .then((data) => data && data.products ? data.products : [])
          );

        fetchRecommendations(seedId)
          .then((prods) => {
            if (renderList(prods)) return;
            if (fallbackHandle) {
              return fetchCollection(fallbackHandle).then((colProds) => {
                if (renderList(colProds)) return;
                list.innerHTML = '';
                syncUpsellHeaderForList(list, cartProductIds);
              });
            }
            list.innerHTML = '';
            syncUpsellHeaderForList(list, cartProductIds);
          })
          .catch(() => {
            list.innerHTML = '';
            syncUpsellHeaderForList(list, cartProductIds);
          });
      });
    };

    const enforceGiftEligibility = (cartState) => {
      const giftBlocks = root.querySelectorAll('[data-free-shipping], [data-gift-selector]');
      if (!giftBlocks.length) return Promise.resolve();

      const ensureCart = cartState && cartState.items
        ? Promise.resolve(cartState)
        : fetchCartState();

      return ensureCart
        .then((state) => {
          if (!state || !state.items) return [];
          const removals = {};
          const queuedRemovals = new Set();

          const collectGiftRules = (block) => {
            const explicitRules = Array.from(block.querySelectorAll('[data-gift-rule]'))
              .map((node) => ({
                variant: String(node.getAttribute('data-gift-variant') || ''),
                threshold: parseInt(node.getAttribute('data-gift-threshold') || '0', 10),
              }))
              .filter((rule) => rule.variant !== '' && Number.isFinite(rule.threshold) && rule.threshold > 0);
            if (explicitRules.length) return explicitRules;

            const legacyRules = [1, 2]
              .map((index) => ({
                variant: String(block.getAttribute(`data-gift-variant-${index}`) || ''),
                threshold: parseInt(block.getAttribute(`data-gift-threshold-${index}`) || '0', 10),
              }))
              .filter((rule) => rule.variant !== '' && Number.isFinite(rule.threshold) && rule.threshold > 0);
            return legacyRules;
          };

          giftBlocks.forEach((block) => {
            const rules = collectGiftRules(block);
            if (!rules.length) return;
            const thresholdByVariant = new Map();
            rules.forEach((rule) => {
              thresholdByVariant.set(rule.variant, rule.threshold);
            });
            const giftVariants = Array.from(thresholdByVariant.keys());

            const cartSubtotal = state.items.reduce((sum, item) => sum + (item.final_line_price || 0), 0);
            const giftsTotal = state.items
              .filter((item) => giftVariants.includes(String(item.id)))
              .reduce((sum, item) => sum + (item.final_line_price || 0), 0);
            const effectiveTotal = cartSubtotal - giftsTotal;

            state.items
              .filter((item) => giftVariants.includes(String(item.id)))
              .forEach((item) => {
                const required = thresholdByVariant.get(String(item.id)) || 0;
                if (required > 0 && effectiveTotal < required && !queuedRemovals.has(item.key)) {
                  queuedRemovals.add(item.key);
                  removals[String(item.variant_id || item.id)] = 0;
                }
              });
          });

          if (!Object.keys(removals).length) return [];
          return postVariantUpdates(removals).then(() => refreshDrawerAfterNestedMutation());
        })
        .catch((err) => {
          console.error('Gift enforcement failed', err);
          return [];
        });
    };

    const initCountdowns = () => {
      const parseTargetDate = (rawValue) => {
        if (!rawValue) return null;
        const value = String(rawValue).trim();
        if (!value) return null;

        if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
          const parts = value.split('-').map((part) => parseInt(part, 10));
          if (parts.length !== 3 || parts.some((part) => !Number.isFinite(part))) return null;
          const [year, month, day] = parts;
          return new Date(year, month - 1, day, 23, 59, 59, 999).getTime();
        }

        const parsed = new Date(value.includes('T') ? value : value.replace(' ', 'T'));
        const timestamp = parsed.getTime();
        return Number.isFinite(timestamp) ? timestamp : null;
      };

      const formatDurationTime = (remainingSeconds) => {
        const minutes = Math.floor(remainingSeconds / 60);
        const seconds = remainingSeconds % 60;
        return `${String(minutes).padStart(2, '0')}m : ${String(seconds).padStart(2, '0')}s`;
      };

      const formatTargetTime = (remainingSeconds) => {
        const total = Math.max(0, remainingSeconds);
        const days = Math.floor(total / 86400);
        const hours = Math.floor((total % 86400) / 3600);
        const minutes = Math.floor((total % 3600) / 60);
        const seconds = total % 60;
        if (days > 0) {
          return `${days}d : ${String(hours).padStart(2, '0')}h : ${String(minutes).padStart(2, '0')}m : ${String(seconds).padStart(2, '0')}s`;
        }
        return `${String(hours).padStart(2, '0')}h : ${String(minutes).padStart(2, '0')}m : ${String(seconds).padStart(2, '0')}s`;
      };

      const timers = root.querySelectorAll('[data-countdown]');
      timers.forEach((el) => {
        const mode = el.dataset.countdownMode === 'date' ? 'date' : 'duration';
        const duration = parseInt(el.dataset.duration || '0', 10);
        const shouldReset = el.dataset.reset === 'true';
        const completeText = el.dataset.completeText || '';
        const targetDate = parseTargetDate(el.dataset.targetDate);
        const display = el.querySelector('[data-countdown-display]');
        const hasDuration = duration > 0;
        const hasTargetDate = Number.isFinite(targetDate);
        if (!display) return;

        let remaining = duration;
        if (el._countdownInterval) {
          clearInterval(el._countdownInterval);
        }

        if ((mode === 'duration' && !hasDuration) || (mode === 'date' && !hasTargetDate)) {
          if (completeText !== '') {
            display.textContent = completeText;
          }
          return;
        }

        const tick = () => {
          if (mode === 'date') {
            const secondsLeft = Math.floor((targetDate - Date.now()) / 1000);
            if (secondsLeft <= 0) {
              if (completeText !== '') {
                display.textContent = completeText;
              } else {
                display.textContent = '00h : 00m : 00s';
              }
              clearInterval(el._countdownInterval);
              return;
            }
            display.textContent = formatTargetTime(secondsLeft);
            return;
          }

          display.textContent = formatDurationTime(remaining);
          remaining -= 1;

          if (remaining < 0) {
            if (shouldReset) {
              remaining = duration;
            } else {
              if (completeText !== '') {
                display.textContent = completeText;
              }
              clearInterval(el._countdownInterval);
            }
          }
        };

        tick();
        el._countdownInterval = setInterval(tick, 1000);
      });
    };

    const getCartLineByElement = (cartState, lineEl) => {
      const items = cartState && Array.isArray(cartState.items) ? cartState.items : [];
      const lineKey = String(lineEl?.getAttribute('data-line-key') || '');
      const variantId = String(lineEl?.getAttribute('data-variant-id') || '');
      return (
        items.find((item) => String(item.key) === lineKey) ||
        items.find((item) => variantId !== '' && String(item.variant_id || item.id) === variantId) ||
        null
      );
    };

    const updateFreeShippingFallback = (cartState) => {
      const items = cartState && Array.isArray(cartState.items) ? cartState.items : [];
      if (!items.length && (!cartState || cartState.item_count !== 0)) return;

      const excludedProductIds = new Set();
      root.querySelectorAll('[data-oneclick-upsell]').forEach((upsellEl) => {
        const config = getOneClickConfig(upsellEl);
        if (config?.excludeSelfFromSubtotal && config.productId) {
          excludedProductIds.add(String(config.productId));
        }
      });

      root.querySelectorAll('[data-free-shipping]').forEach((block) => {
        const threshold = normalizeMoneyCents(block.getAttribute('data-threshold'));
        const giftVariants = [1, 2]
          .map((index) => String(block.getAttribute(`data-gift-variant-${index}`) || ''))
          .filter(Boolean);
        const subtotal = items.reduce((sum, item) => {
          const itemVariant = String(item.variant_id || item.id || '');
          const itemProduct = String(item.product_id || '');
          if (giftVariants.includes(itemVariant)) return sum;
          if (excludedProductIds.has(itemProduct)) return sum;
          return sum + normalizeMoneyCents(item.final_line_price);
        }, 0);
        const progress = threshold > 0 ? Math.min(100, Math.max(0, Math.round((subtotal / threshold) * 100))) : 100;
        const remaining = Math.max(0, threshold - subtotal);
        const achieved = threshold <= 0 || remaining <= 0;
        const fill = block.querySelector('.kozler-free-shipping__fill');
        const bar = block.querySelector('.kozler-free-shipping__bar');
        const indicator = block.querySelector('.kozler-free-shipping__indicator');
        const text = block.querySelector('.kozler-free-shipping__text');

        block.style.setProperty('--fs-progress', `${progress}%`);
        if (bar) bar.style.setProperty('--fs-progress', `${progress}%`);
        if (fill) fill.style.width = `${progress}%`;
        if (indicator) indicator.style.left = `${progress}%`;
        if (text) {
          if (achieved) {
            text.textContent = block.getAttribute('data-goal-text-success') || 'You unlocked FREE shipping!';
          } else {
            const prefix = block.getAttribute('data-goal-text-prefix') || "You're";
            const suffix = block.getAttribute('data-goal-text-suffix') || 'away from FREE shipping!';
            text.innerHTML = `${escapeHtmlAttr(prefix)} <strong>${formatMoney(remaining)}</strong> ${escapeHtmlAttr(suffix)}`;
          }
        }

        [1, 2].forEach((index) => {
          const giftThreshold = normalizeMoneyCents(block.getAttribute(`data-gift-threshold-${index}`));
          const dot = block.querySelectorAll('.kozler-free-shipping__gift-dot')[index - 1];
          if (dot && giftThreshold > 0) {
            dot.classList.toggle('is-active', subtotal >= giftThreshold);
          }
          block.setAttribute(`data-gift-eligible-${index}`, giftThreshold > 0 && subtotal >= giftThreshold ? 'true' : 'false');
        });
      });
    };

    const applyCartStateFallback = (cartState) => {
      if (!cartState || !Array.isArray(cartState.items)) return;
      const activeKeys = new Set(cartState.items.map((item) => String(item.key || '')));

      root.querySelectorAll('[data-cart-subtotal]').forEach((subtotalEl) => {
        subtotalEl.innerHTML = formatMoney(cartState.total_price || 0);
      });

      content.querySelectorAll('[data-cart-line]').forEach((lineEl) => {
        const item = getCartLineByElement(cartState, lineEl);
        const lineKey = String(lineEl.getAttribute('data-line-key') || '');
        if (!item || (lineKey && !activeKeys.has(lineKey))) {
          lineEl.remove();
          return;
        }
        lineEl.setAttribute('data-line-key', item.key);
        lineEl.setAttribute('data-variant-id', String(item.variant_id || item.id || ''));
        const qtyInput = lineEl.querySelector('[data-qty-input]');
        if (qtyInput) {
          const nextQuantity = String(item.quantity || 0);
          qtyInput.value = nextQuantity;
        }
        const currentPrice = lineEl.querySelector('.kozler-cart-item__price--current');
        if (currentPrice) currentPrice.innerHTML = formatMoney(item.final_line_price || 0);
        const comparePrice = lineEl.querySelector('.kozler-cart-item__price--compare');
        if (comparePrice && item.original_line_price > item.final_line_price) {
          comparePrice.innerHTML = formatMoney(item.original_line_price);
        }
      });

      updateFreeShippingFallback(cartState);
      updateTermsState();
      updateHeaderActions(cartState.item_count || 0);
      setLastKnownCartProductIds(cartState);
      content.querySelectorAll('.kozler-cart-quantity.is-loading, .kozler-cart-quantity.is-locked').forEach((control) => {
        setQuantityControlLocked(control, false);
      });
      content.querySelectorAll('.is-loading[aria-busy="true"]').forEach((control) => {
        stopControlLoading(control);
      });
    };

    const completeCartRefresh = (cartState, updatedIsEmpty = false, attempt = 0) => {
      const itemCount = cartState?.item_count || 0;
      setLastKnownCartProductIds(cartState);
      updateHeaderActions(itemCount);
      if (itemCount === 0 && !updatedIsEmpty && attempt < 2) {
        return new Promise((resolve) => {
          setTimeout(resolve, 120);
        }).then(() => refreshDrawer(attempt + 1));
      }
      return syncOneClickUpsells(cartState).then((oneClickResult) => {
        if (oneClickResult?.mutated) return null;
        renderSmartUpsells(cartState);
        syncRecommendedUpsellHeaders(cartState);
        const autoAddPromise = enforceAutoAddRules(cartState);
        const giftPromise = enforceGiftEligibility(cartState);
        dispatchLegacyCartEvents(cartState);
        document.dispatchEvent(new CustomEvent('kozler:cart:refreshed', { detail: { count: itemCount, cart: cartState } }));
        return Promise.all([autoAddPromise, giftPromise].filter(Boolean));
      });
    };

    let refreshPromise = null;

    const refreshDrawer = (attempt = 0) => {
      if (attempt === 0 && refreshPromise) {
        return refreshPromise;
      }

      if (attempt === 0) {
        setBusy(true);
      }

      const request = fetchDrawerSectionHtml()
        .then((html) => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          const updatedRoot = doc.querySelector(`#kozler-cart-${sectionId}`);
          const updatedContent = doc.querySelector(`#kozler-cart-${sectionId} [data-cart-content]`);
          const updatedBadge = doc.querySelector(`#kozler-cart-${sectionId} [data-cart-count]`);
          const updatedIsEmpty =
            !!updatedContent &&
            (updatedContent.classList.contains('kozler-cart__content--empty') ||
              !!updatedContent.querySelector('.kozler-cart__empty'));
          let nextCount = null;

          if (!updatedContent) {
            throw new Error(`Kozler cart section refresh did not return #kozler-cart-${sectionId} [data-cart-content]`);
          }

          if (updatedRoot) {
            Array.from(updatedRoot.attributes).forEach((attr) => {
              if (attr.name.startsWith('data-')) {
                root.setAttribute(attr.name, attr.value);
              }
            });
          }

          if (content) {
            content.innerHTML = updatedContent.innerHTML;
            content.className = updatedContent.className;
            Array.from(updatedContent.attributes).forEach((attr) => {
              if (attr.name.startsWith('data-')) {
                content.setAttribute(attr.name, attr.value);
              }
            });
            syncRememberedUpsellHeaders();
          }

          if (updatedBadge) {
            const updatedValueEl = updatedBadge.querySelector('[data-cart-count-value]');
            nextCount =
              updatedBadge.getAttribute('data-count-value') ||
              (updatedValueEl ? updatedValueEl.textContent.trim() : '') ||
              updatedBadge.textContent.trim();
          } else if (updatedRoot) {
            nextCount = updatedRoot.getAttribute('data-cart-count');
          }

          if (nextCount !== null) {
            updateHeaderActions(nextCount);
          }

          updateTermsState();
          initCountdowns();
          return fetchCartState().then((cartState) => completeCartRefresh(cartState, updatedIsEmpty, attempt));
        })
        .catch((error) => {
          console.error('Cart section refresh failed; applying live cart fallback', error);
          return fetchCartState().then((cartState) => {
            applyCartStateFallback(cartState);
            return completeCartRefresh(cartState, false, attempt);
          });
        })
        .finally(() => {
          setBusy(false);
        });

      if (attempt === 0) {
        refreshPromise = request.finally(() => {
          refreshPromise = null;
        });
        return refreshPromise;
      }

      return request;
    };

    const refreshDrawerAfterNestedMutation = () => (refreshPromise ? refreshDrawer(1) : refreshDrawer());

    const refreshDrawerAfterMutation = () => {
      const activeRefresh = refreshPromise;
      if (activeRefresh) {
        return activeRefresh.then(() => refreshDrawer());
      }
      return refreshDrawer();
    };

    const normalizeQuantity = (quantity) => {
      const parsed = parseInt(quantity, 10);
      return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
    };

    const getQuantityControl = (controlEl) => {
      if (!controlEl) return null;
      if (controlEl.matches?.('.kozler-cart-quantity')) return controlEl;
      return controlEl.closest?.('.kozler-cart-quantity') || null;
    };

    const setQuantityControlLocked = (controlEl, locked) => {
      const quantityControl = getQuantityControl(controlEl);
      if (!quantityControl) return;
      quantityControl.classList.toggle('is-loading', locked);
      quantityControl.classList.toggle('is-locked', locked);
      quantityControl.setAttribute('aria-busy', locked ? 'true' : 'false');
      quantityControl.querySelectorAll('button, input, select').forEach((field) => {
        field.disabled = locked;
      });
    };

    const suppressAutoAddForZeroQuantity = (lineKey, controlEl) => {
      if (!lineKey) return;
      const parentLine =
        controlEl?.closest?.('[data-cart-line]') ||
        Array.from(content.querySelectorAll('[data-cart-line]')).find((line) => line.getAttribute('data-line-key') === lineKey);
      const variantId = parentLine?.getAttribute('data-variant-id');
      if (variantId) {
        suppressAutoAddForVariant(variantId);
      }
    };

    const getCartLinePayload = (lineKey, quantity, controlEl) => {
      const parentLine =
        controlEl?.closest?.('[data-cart-line]') ||
        Array.from(content.querySelectorAll('[data-cart-line]')).find((line) => line.getAttribute('data-line-key') === lineKey);
      const lineNumber = parseInt(parentLine?.getAttribute('data-cart-line') || '', 10);

      if (Number.isFinite(lineNumber) && lineNumber > 0) {
        return { line: lineNumber, quantity };
      }

      return { id: lineKey, quantity };
    };

    const updateQuantity = (lineKey, quantity, controlEl) => {
      if (!lineKey) return;
      const nextQuantity = normalizeQuantity(quantity);
      if (nextQuantity === 0) {
        suppressAutoAddForZeroQuantity(lineKey, controlEl);
      }
      const quantityControl = getQuantityControl(controlEl);
      if (quantityControl) {
        setQuantityControlLocked(quantityControl, true);
      } else {
        startControlLoading(controlEl);
      }
      setBusy(true);
      return fetchNoStore(getCartAjaxEndpoint('change'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', [KOZLER_CART_MANUAL_HEADER]: 'drawer' },
        body: JSON.stringify(getCartLinePayload(lineKey, nextQuantity, controlEl)),
      })
        .then((response) => {
          if (!response.ok) {
            return response.json().catch(() => ({})).then((error) => {
              throw new Error(error.description || error.message || `Quantity update failed with ${response.status}`);
            });
          }
          return response.json().catch(() => null);
        })
        .then(() => refreshDrawerAfterMutation())
        .catch((error) => {
          console.error('Quantity update failed', error);
          if (quantityControl) {
            setQuantityControlLocked(quantityControl, false);
          } else {
            stopControlLoading(controlEl);
          }
        })
        .finally(() => {
          setBusy(false);
        });
    };

    const updateVariantQuantities = (updates, controlEl, options = {}) => {
      const shouldRefresh = options.refresh !== false;
      const onFailure = typeof options.onFailure === 'function' ? options.onFailure : null;

      startControlLoading(controlEl);
      setBusy(true);
      return postVariantUpdates(updates)
        .then(() => {
          if (shouldRefresh) {
            return refreshDrawerAfterMutation();
          }
          return null;
        })
        .catch((error) => {
          console.error('Variant update failed', error);
          if (onFailure) {
            onFailure(error);
          }
          stopControlLoading(controlEl);
        })
        .finally(() => {
          setBusy(false);
        });
    };

    const addVariant = (variantId, controlEl, quantity = 1, options = {}) => {
      if (!variantId) return;
      const shouldRefresh = options.refresh !== false;
      const onFailure = typeof options.onFailure === 'function' ? options.onFailure : null;
      startControlLoading(controlEl);
      setBusy(true);
      return fetchNoStore(getCartAjaxEndpoint('add'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', [KOZLER_CART_MANUAL_HEADER]: 'drawer' },
        body: JSON.stringify({ id: Number(variantId), quantity: Math.max(1, quantity) }),
      })
        .then(() => {
          if (shouldRefresh) {
            return refreshDrawerAfterMutation();
          }
          return null;
        })
        .catch((error) => {
          console.error('Add to cart failed', error);
          if (onFailure) {
            onFailure(error);
          }
          stopControlLoading(controlEl);
        })
        .finally(() => {
          setBusy(false);
        });
    };

    const enforceAutoAddRules = (cartState) => {
      const rules = collectAutoAddRules();
      if (!rules.length) return;

      const ensureCart = cartState && cartState.items ? Promise.resolve(cartState) : fetchCartState();

      return ensureCart
        .then((state) => {
          if (!state || !state.items) return;
          loadSuppressedRules(state);
          const variantIds = state.items.map((item) => String(item.variant_id));
          const actions = [];
          let suppressedChanged = false;
          const rulesByTarget = new Map();

          rules.forEach((rule) => {
            if (!variantIds.includes(rule.trigger) && autoAddSuppressed.has(rule.id)) {
              autoAddSuppressed.delete(rule.id);
              suppressedChanged = true;
            }
            const targetKey = String(rule.target);
            if (!rulesByTarget.has(targetKey)) {
              rulesByTarget.set(targetKey, []);
            }
            rulesByTarget.get(targetKey).push(rule);
          });

          if (suppressedChanged) {
            updateCartAttributes({ [AUTO_ADD_SUPPRESS_ATTR]: Array.from(autoAddSuppressed).join(',') });
          }

          rulesByTarget.forEach((targetRules, targetVariantId) => {
            const targetInCart = variantIds.includes(targetVariantId);
            const activeTriggerRules = targetRules.filter((rule) => variantIds.includes(rule.trigger));

            // If no triggers for this target are currently in cart, remove the auto-added target.
            if (!activeTriggerRules.length) {
              if (targetInCart) {
                actions.push(() => removeVariant(Number(targetVariantId), null, { refresh: false }));
              }
              return;
            }

            if (targetInCart) return;

            const eligibleRules = activeTriggerRules.filter((rule) => !autoAddSuppressed.has(rule.id));
            if (!eligibleRules.length) return;

            const desiredQty = eligibleRules.reduce((maxQty, rule) => {
              const nextQty = Math.max(1, parseInt(rule.qty, 10) || 1);
              return Math.max(maxQty, nextQty);
            }, 1);

            actions.push(() => addVariant(Number(targetVariantId), null, desiredQty, { refresh: false }));
          });

          if (!actions.length) return;

          return actions
            .reduce((promise, runAction) => promise.then(() => runAction()), Promise.resolve())
            .then(() => refreshDrawerAfterNestedMutation());
        })
        .catch((error) => console.error('Auto-add rules failed', error));
    };

    const removeVariant = (variantId, controlEl, options = {}) => {
      if (!variantId) return;
      return updateVariantQuantities({ [variantId]: 0 }, controlEl, options);
    };

    const removeLine = (lineKey, controlEl) => updateQuantity(lineKey, 0, controlEl);

    const swapVariant = (lineKey, nextId, quantity, sellingPlan, properties, controlEl) => {
      if (!lineKey || !nextId) return;
      setBusy(true);
      startControlLoading(controlEl);

      if (properties && typeof properties === 'object') {
        Object.keys(properties).forEach((key) => {
          if (properties[key] === '' || properties[key] === null) {
            delete properties[key];
          }
        });
      }

      const addPayload = { id: nextId, quantity };
      if (sellingPlan) addPayload.selling_plan = sellingPlan;
      if (properties && Object.keys(properties).length > 0) addPayload.properties = properties;

      return fetchNoStore(getCartAjaxEndpoint('change'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', [KOZLER_CART_MANUAL_HEADER]: 'drawer' },
        body: JSON.stringify({ id: lineKey, quantity: 0 }),
      })
        .then(() =>
          fetchNoStore(getCartAjaxEndpoint('add'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', [KOZLER_CART_MANUAL_HEADER]: 'drawer' },
            body: JSON.stringify(addPayload),
          })
        )
        .then(() => refreshDrawerAfterMutation())
        .catch((error) => {
          console.error('Variant change failed', error);
          stopControlLoading(controlEl);
        })
        .finally(() => {
          setBusy(false);
        });
    };

    const updateNote = (note) => {
      setBusy(true);
      return fetchNoStore(getCartAjaxEndpoint('update'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', [KOZLER_CART_MANUAL_HEADER]: 'drawer' },
        body: JSON.stringify({ note }),
      })
        .then(() => refreshDrawerAfterMutation())
        .catch((error) => {
          console.error('Cart note failed', error);
        })
        .finally(() => {
          setBusy(false);
        });
    };

    const getCartAttributeValue = (fieldBlock) => {
      if (!fieldBlock) return '';
      const fieldType = fieldBlock.dataset.fieldType || 'text';
      if (fieldType === 'checkbox') {
        const checked = Array.from(fieldBlock.querySelectorAll('input[type="checkbox"][data-cart-attribute-input]:checked'))
          .map((input) => input.value.trim())
          .filter(Boolean);
        return checked.length ? checked.join(' | ') : '';
      }
      if (fieldType === 'radio') {
        const checked = fieldBlock.querySelector('input[type="radio"][data-cart-attribute-input]:checked');
        return checked ? checked.value.trim() : '';
      }
      const input = fieldBlock.querySelector('[data-cart-attribute-input]');
      return input ? input.value.trim() : '';
    };

    const updateCartAttributeField = (fieldBlock) => {
      if (!fieldBlock) return;
      const attributeKey = fieldBlock.dataset.attributeKey;
      if (!attributeKey) return;
      const nextValue = getCartAttributeValue(fieldBlock);
      startControlLoading(fieldBlock);
      return updateCartAttributes({ [attributeKey]: nextValue })
        .finally(() => {
          stopControlLoading(fieldBlock);
        });
    };

    const validateCartAttributeFields = () => {
      const fields = content.querySelectorAll('[data-cart-attribute-field]');
      if (!fields.length) return true;
      let firstInvalid = null;
      fields.forEach((field) => {
        const required = field.dataset.required === 'true';
        if (!required) return;
        const value = getCartAttributeValue(field);
        const error = field.querySelector('[data-cart-attribute-error]');
        if (!value) {
          if (error) error.hidden = false;
          field.classList.add('has-error');
          if (!firstInvalid) firstInvalid = field;
          return;
        }
        if (error) error.hidden = true;
        field.classList.remove('has-error');
      });
      if (firstInvalid) {
        const focusEl = firstInvalid.querySelector('[data-cart-attribute-input]');
        if (focusEl) focusEl.focus();
        return false;
      }
      return true;
    };

    const collapseMinimalQtyControls = (exceptEl = null) => {
      const expandedControls = content.querySelectorAll('[data-qty-minimal].is-expanded');
      expandedControls.forEach((control) => {
        if (exceptEl && (control === exceptEl || control.contains(exceptEl))) return;
        control.classList.remove('is-expanded');
        control.setAttribute('data-qty-expanded', 'false');
      });
    };

    const expandMinimalQtyControl = (control) => {
      if (!control) return;
      collapseMinimalQtyControls(control);
      control.classList.add('is-expanded');
      control.setAttribute('data-qty-expanded', 'true');
    };

    content.addEventListener('click', (event) => {
      const qtyButton = event.target.closest('[data-qty-button]');
      const removeButton = event.target.closest('[data-remove-line]');
      const upsellQtyButton = event.target.closest('[data-upsell-qty-button]');
      const breakToggle = event.target.closest('[data-quantity-break-toggle]');
      const minimalQtyControl = event.target.closest('[data-qty-minimal]');

      if (minimalQtyControl && !minimalQtyControl.classList.contains('is-expanded')) {
        const clickedQtyButton = !!event.target.closest('[data-qty-button]');
        if (!clickedQtyButton) {
          event.preventDefault();
          expandMinimalQtyControl(minimalQtyControl);
          const qtyInput = minimalQtyControl.querySelector('[data-qty-input]');
          if (qtyInput) {
            qtyInput.focus({ preventScroll: true });
            if (typeof qtyInput.select === 'function') qtyInput.select();
          }
          return;
        }
      } else if (!minimalQtyControl) {
        collapseMinimalQtyControls();
      }

      if (qtyButton) {
        if (busyCount > 0 || qtyButton.disabled) return;
        const parentLine = qtyButton.closest('[data-cart-line]');
        const input = parentLine?.querySelector('[data-qty-input]');
        const lineKey = parentLine?.getAttribute('data-line-key');
        if (!lineKey || !input) return;
        const qtyWrapper = qtyButton.closest('.kozler-cart-quantity') || qtyButton;
        if (qtyWrapper.classList?.contains('is-loading') || qtyWrapper.classList?.contains('is-locked')) return;
        const qtyControl = qtyButton.closest('[data-qty-minimal]');
        if (qtyControl) {
          expandMinimalQtyControl(qtyControl);
        }

        const currentQty = parseInt(input.value, 10) || 0;
        const nextQty = qtyButton.dataset.direction === 'up' ? currentQty + 1 : Math.max(0, currentQty - 1);
        input.value = String(nextQty);
        updateQuantity(lineKey, nextQty, qtyWrapper);
        return;
      }

      if (upsellQtyButton) {
        const qtyWrapper = upsellQtyButton.closest('[data-upsell-qty]');
        const input = qtyWrapper?.querySelector('[data-upsell-qty-input]');
        if (!input) return;
        const currentQty = parseInt(input.value, 10) || 1;
        const nextQty = upsellQtyButton.dataset.direction === 'up' ? currentQty + 1 : Math.max(1, currentQty - 1);
        input.value = nextQty;
        return;
      }

      if (breakToggle) {
        if (busyCount > 0 || breakToggle.disabled) return;
        const parentLine = breakToggle.closest('[data-cart-line]');
        const lineKey = parentLine?.getAttribute('data-line-key');
        if (!lineKey) return;

        const baseRaw = breakToggle.closest('[data-quantity-breaks]')?.getAttribute('data-break-base-qty') || '1';
        const baseQty = parseInt(baseRaw, 10);
        const defaultQty = Number.isFinite(baseQty) ? Math.max(1, baseQty) : 1;
        const breakQtyRaw = breakToggle.getAttribute('data-break-qty');
        const breakQty = parseInt(breakQtyRaw, 10);
        if (!Number.isFinite(breakQty) || breakQty < 1) return;

        const isActive = breakToggle.classList.contains('is-active') || breakToggle.getAttribute('aria-pressed') === 'true';
        const nextQty = isActive ? defaultQty : breakQty;
        const qtyInputEl = parentLine.querySelector('[data-qty-input]');
        const currentQty = qtyInputEl ? parseInt(qtyInputEl.value, 10) : null;
        if (currentQty === nextQty) return;

        if (qtyInputEl) qtyInputEl.value = String(nextQty);
        updateQuantity(lineKey, nextQty, breakToggle);
        return;
      }

      if (removeButton) {
        if (busyCount > 0 || removeButton.disabled) return;
        const lineKey = removeButton.getAttribute('data-line-key') || removeButton.closest('[data-cart-line]')?.getAttribute('data-line-key');
        if (!lineKey) return;
        removeLine(lineKey, removeButton);
      }
    });

    content.addEventListener('submit', (event) => {
      const form = event.target.closest('.kozler-cart__summary-form');
      if (!form) return;
      if (busyCount > 0) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      const validNote = validateNoteRequirement();
      const validTerms = validateTermsRequirement();
      const validFields = validateCartAttributeFields();
      if (!validNote || !validTerms || !validFields) {
        event.preventDefault();
        event.stopPropagation();
      }
    });

    content.addEventListener('change', (event) => {
      const termsCheckbox = event.target.closest('[data-terms-checkbox]');
      const attributeInput = event.target.closest('[data-cart-attribute-input]');
      const breakOption = event.target.closest('[data-quantity-break-option]');
      const qtyInput = event.target.closest('[data-qty-input]');
      const upsellQtyInput = event.target.closest('[data-upsell-qty-input]');
      const variantSelect = event.target.closest('[data-variant-select]');
      const upsellVariantSelect = event.target.closest('[data-upsell-variant-select]');
      const upsellToggle = event.target.closest('[data-upsell-toggle]');

      if (termsCheckbox) {
        const termsBlock = termsCheckbox.closest('[data-terms-block]');
        const error = termsBlock?.querySelector('[data-terms-error]');
        if (error) error.hidden = true;
        termsBlock?.classList.remove('has-error');
        updateTermsState();
        return;
      }

      if (attributeInput) {
        const fieldBlock = attributeInput.closest('[data-cart-attribute-field]');
        if (!fieldBlock) return;
        const error = fieldBlock.querySelector('[data-cart-attribute-error]');
        if (error) error.hidden = true;
        fieldBlock.classList.remove('has-error');
        updateCartAttributeField(fieldBlock);
        return;
      }

      if (breakOption) {
        if (busyCount > 0 || breakOption.disabled) return;
        const parentLine = breakOption.closest('[data-cart-line]');
        const lineKey = parentLine?.getAttribute('data-line-key');
        if (!lineKey) return;
        const rawQty = breakOption.getAttribute('data-break-qty') || breakOption.value;
        const nextQty = parseInt(rawQty, 10);
        if (!Number.isFinite(nextQty) || nextQty < 0) return;
        const qtyInputEl = parentLine?.querySelector('[data-qty-input]');
        const currentQty = qtyInputEl ? parseInt(qtyInputEl.value, 10) : null;
        if (currentQty === nextQty) return;
        const breakLabel = breakOption.closest('.kozler-cart-item__break') || breakOption;
        if (qtyInputEl) qtyInputEl.value = String(nextQty);
        updateQuantity(lineKey, nextQty, breakLabel);
        return;
      }

      if (qtyInput) {
        if (busyCount > 0 || qtyInput.disabled) return;
        const parentLine = qtyInput.closest('[data-cart-line]');
        const lineKey = parentLine?.getAttribute('data-line-key');
        if (!lineKey) return;
        const minimalControl = qtyInput.closest('[data-qty-minimal]');
        if (minimalControl) {
          expandMinimalQtyControl(minimalControl);
        }
        const fallbackRaw = parseInt(qtyInput.getAttribute('value'), 10);
        const fallbackQty = Number.isFinite(fallbackRaw) ? fallbackRaw : 1;
        const parsed = parseInt(qtyInput.value, 10);
        if (!Number.isFinite(parsed)) {
          qtyInput.value = String(fallbackQty);
          return;
        }
        const nextQty = Math.max(0, parsed);
        qtyInput.value = String(nextQty);
        if (nextQty === fallbackQty) return;
        const qtyWrapper = qtyInput.closest('.kozler-cart-quantity') || qtyInput;
        updateQuantity(lineKey, nextQty, qtyWrapper);
        return;
      }

      if (upsellQtyInput) {
        const parsed = parseInt(upsellQtyInput.value, 10);
        const nextQty = Number.isFinite(parsed) ? Math.max(1, parsed) : 1;
        upsellQtyInput.value = String(nextQty);
        return;
      }

      if (upsellToggle) {
        const upsellItem = upsellToggle.closest('[data-upsell-item]');
        const upsellList = upsellItem?.closest('.kozler-cart-upsell__list');
        const variantSelect = upsellItem?.querySelector('[data-upsell-variant-select]');
        const qtyInput = upsellItem?.querySelector('[data-upsell-qty-input]');
        const variantId = variantSelect ? variantSelect.value : upsellToggle.getAttribute('data-variant-id');
        const productId = upsellItem?.getAttribute('data-upsell-product-id');
        if (!variantId) {
          upsellToggle.checked = false;
          return;
        }

        if (upsellToggle.checked) {
          const qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
          if (upsellList && productId && shouldHideUpsellWhenAdded(upsellList)) {
            rememberUpsellProductId(upsellList, productId);
            syncUpsellHeaderForList(upsellList, new Set([String(productId)]));
          }
          addVariant(Number(variantId), upsellToggle, qty, {
            onFailure: () => {
              upsellToggle.checked = false;
              if (upsellList && productId && shouldHideUpsellWhenAdded(upsellList)) {
                forgetUpsellProductId(upsellList, productId);
                syncUpsellHeaderForList(upsellList, new Set());
              }
              syncUpsellItemState(upsellItem);
            },
          });
        } else {
          removeVariant(Number(variantId), upsellToggle, {
            onFailure: () => {
              upsellToggle.checked = true;
              syncUpsellItemState(upsellItem);
            },
          });
        }
        syncUpsellItemState(upsellItem);
        return;
      }

      if (variantSelect) {
        const parentLine = variantSelect.closest('[data-cart-line]');
        const lineKey = parentLine?.getAttribute('data-line-key');
        const currentVariant = parentLine?.getAttribute('data-variant-id');
        const qtyInput = parentLine?.querySelector('[data-qty-input]');
        if (!lineKey || !qtyInput) return;

        const quantity = parseInt(qtyInput.value, 10) || 1;
        const nextVariantId = parseInt(variantSelect.value, 10);
        if (!nextVariantId) return;
        if (String(currentVariant) === String(nextVariantId)) return;

        const sellingPlan = parentLine?.getAttribute('data-selling-plan') || null;
        let properties = null;

        if (parentLine?.dataset.lineProperties) {
          try {
            properties = JSON.parse(parentLine.dataset.lineProperties);
          } catch (error) {
            properties = null;
          }
        }

        swapVariant(lineKey, nextVariantId, quantity, sellingPlan, properties, variantSelect);
        return;
      }

      if (upsellVariantSelect) {
        const upsellItem = upsellVariantSelect.closest('[data-upsell-item]');
        const upsellList = upsellItem?.closest('.kozler-cart-upsell__list');
        const addButton = upsellItem?.querySelector('[data-upsell-add]');
        const toggleInput = upsellItem?.querySelector('[data-upsell-toggle]');
        if (addButton) {
          addButton.setAttribute('data-variant-id', upsellVariantSelect.value);
        }
        if (toggleInput) {
          toggleInput.setAttribute('data-variant-id', upsellVariantSelect.value);
        }
        const selectedOption = upsellVariantSelect.options[upsellVariantSelect.selectedIndex];
        if (selectedOption) {
          syncUpsellPrice(
            upsellItem,
            selectedOption.getAttribute('data-price'),
            selectedOption.getAttribute('data-compare-at-price')
          );
          if (addButton) {
            const showPriceInLabel = upsellList?.dataset.buttonShowPriceInLabel === 'true';
            const baseLabel = addButton.getAttribute('data-base-label') || upsellList?.dataset.buttonLabel || 'Add';
            addButton.textContent = getUpsellAddButtonLabel(baseLabel, selectedOption.getAttribute('data-price'), showPriceInLabel);
          }
          if (toggleInput && upsellList) {
            const toggleLabel = upsellItem.querySelector('[data-upsell-toggle-label]');
            if (toggleLabel) {
              const baseLabel = toggleLabel.getAttribute('data-base-label') || upsellList.dataset.buttonLabel || 'Add';
              const showPriceInLabel = upsellList.dataset.buttonShowPriceInLabel === 'true';
              const nextAddLabel = getUpsellAddButtonLabel(baseLabel, selectedOption.getAttribute('data-price'), showPriceInLabel);
              toggleLabel.setAttribute('data-add-label', nextAddLabel);
            }
            syncUpsellItemState(upsellItem);
          }
        }
        return;
      }
    });

    content.addEventListener('input', (event) => {
      const noteField = event.target.closest('[data-cart-note]');
      if (noteField) {
        clearTimeout(noteTimer);
        noteTimer = setTimeout(() => updateNote(noteField.value), 400);
        return;
      }
    });

    content.addEventListener('focusin', (event) => {
      const qtyInput = event.target.closest('[data-qty-input]');
      if (!qtyInput) return;
      const minimalControl = qtyInput.closest('[data-qty-minimal]');
      if (!minimalControl) return;
      expandMinimalQtyControl(minimalControl);
    });

    content.addEventListener('click', (event) => {
      const noteToggle = event.target.closest('[data-note-toggle]');
      const checkoutButton = event.target.closest('[data-checkout-button]');
      const tooltipTrigger = event.target.closest('[data-terms-tooltip-trigger]');

      if (tooltipTrigger) {
        event.preventDefault();
        event.stopPropagation();
        const tooltip = tooltipTrigger.closest('[data-terms-tooltip]');
        if (!tooltip) return;
        const isOpen = tooltip.classList.toggle('is-open');
        tooltipTrigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        return;
      }

      if (noteToggle) {
        const noteBlock = noteToggle.closest('[data-note-block]');
        const body = noteBlock?.querySelector('.kozler-cart__note-body');
        if (!body) return;
        const isHidden = body.hasAttribute('hidden');
        if (isHidden) {
          body.removeAttribute('hidden');
          noteToggle.setAttribute('aria-expanded', 'true');
        } else {
          body.setAttribute('hidden', '');
          noteToggle.setAttribute('aria-expanded', 'false');
        }
        return;
      }

      if (checkoutButton) {
        if (busyCount > 0) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        const validNote = validateNoteRequirement();
        const validTerms = validateTermsRequirement();
        const validFields = validateCartAttributeFields();
        if (!validNote || !validTerms || !validFields) {
          event.preventDefault();
          event.stopPropagation();
        }
      }
    });

    if (shareButton) {
      shareButton.addEventListener('click', (event) => {
        event.preventDefault();
        shareCart();
      });
    }
    if (clearButton) {
      clearButton.addEventListener('click', (event) => {
        event.preventDefault();
        clearCart();
      });
    }

    initCountdowns();
    fetchCartState()
      .then((cartState) => {
        setLastKnownCartProductIds(cartState);
        updateHeaderActions(cartState?.item_count || 0);
        return syncOneClickUpsells(cartState).then((oneClickResult) => {
          if (oneClickResult?.mutated) return null;
          return cartState;
        });
      })
      .then((cartState) => {
        if (!cartState) return null;
        renderSmartUpsells(cartState);
        syncRecommendedUpsellHeaders(cartState);
        enforceAutoAddRules(cartState);
        return enforceGiftEligibility(cartState);
      })
      .catch(() => {});

    content.addEventListener('click', (event) => {
      const giftChoiceToggle = event.target.closest('[data-gift-choice-toggle]');
      const giftChoiceClose = event.target.closest('[data-gift-choice-close]');
      const giftButton = event.target.closest('[data-free-gift-add]');
      const upsellButton = event.target.closest('[data-upsell-add]');
      const oneClickToggle = event.target.closest('[data-oneclick-toggle]');

      if (giftChoiceToggle) {
        event.preventDefault();
        const selector = giftChoiceToggle.closest('[data-gift-selector]');
        if (!selector) return;
        const choiceDisplay = selector.getAttribute('data-gift-choice-display') || 'dropdown';
        const choiceCard = giftChoiceToggle.closest('.kozler-gift-selector__item--choice');
        let isOpen = false;

        if (choiceDisplay === 'inline') {
          if (!choiceCard) return;
          isOpen = !choiceCard.classList.contains('is-choice-open');
          choiceCard.classList.toggle('is-choice-open', isOpen);
        } else {
          const panel = selector.querySelector('[data-gift-choice-panel]');
          if (!panel) return;
          isOpen = panel.classList.toggle('is-open');
        }

        const toggles = selector.querySelectorAll('[data-gift-choice-toggle]');
        toggles.forEach((toggleEl) => {
          toggleEl.classList.toggle('is-open', isOpen);
          toggleEl.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        });
        return;
      }

      if (giftChoiceClose) {
        event.preventDefault();
        const selector = giftChoiceClose.closest('[data-gift-selector]');
        if (!selector) return;
        const choiceDisplay = selector.getAttribute('data-gift-choice-display') || 'dropdown';

        if (choiceDisplay === 'inline') {
          const choiceCard = giftChoiceClose.closest('.kozler-gift-selector__item--choice');
          if (choiceCard) {
            choiceCard.classList.remove('is-choice-open');
          }
        } else {
          const panel = selector.querySelector('[data-gift-choice-panel]');
          panel?.classList.remove('is-open');
        }

        const toggles = selector.querySelectorAll('[data-gift-choice-toggle]');
        toggles.forEach((toggleEl) => {
          toggleEl.classList.remove('is-open');
          toggleEl.setAttribute('aria-expanded', 'false');
        });
        return;
      }

      if (giftButton) {
        event.preventDefault();
        const variantId = giftButton.getAttribute('data-gift-variant');
        if (!variantId) return;
        addVariant(Number(variantId), giftButton, 1);
        return;
      }
      if (upsellButton) {
        event.preventDefault();
        const upsellItem = upsellButton.closest('[data-upsell-item]');
        const upsellList = upsellItem?.closest('.kozler-cart-upsell__list');
        const variantSelect = upsellItem?.querySelector('[data-upsell-variant-select]');
        const qtyInput = upsellItem?.querySelector('[data-upsell-qty-input]');
        const qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
        const variantId = variantSelect ? variantSelect.value : upsellButton.getAttribute('data-variant-id');
        const productId = upsellItem?.getAttribute('data-upsell-product-id');
        if (upsellList && productId && shouldHideUpsellWhenAdded(upsellList)) {
          rememberUpsellProductId(upsellList, productId);
          syncUpsellHeaderForList(upsellList, new Set([String(productId)]));
        }
        if (!variantId) return;
        addVariant(Number(variantId), upsellButton, qty, {
          onFailure: () => {
            if (!upsellList || !productId || !shouldHideUpsellWhenAdded(upsellList)) return;
            forgetUpsellProductId(upsellList, productId);
            syncUpsellHeaderForList(upsellList, new Set());
          },
        });
        return;
      }
      if (oneClickToggle) {
        const checked = oneClickToggle.checked;
        const oneClickUpsell = oneClickToggle.closest('[data-oneclick-upsell]');
        const variantId = oneClickUpsell?.getAttribute('data-variant-id');
        if (!variantId || !oneClickUpsell) return;
        if (checked) {
          addVariant(Number(variantId), oneClickToggle, 1);
        } else {
          removeOneClickProduct(oneClickUpsell, oneClickToggle);
        }
        return;
      }
    });

    const OPEN_STYLE_GROW = 'grow';
    let closeTimer = null;

    const isGrowStyle = () => root.dataset.openStyle === OPEN_STYLE_GROW;

    const getDuration = (value, fallback) => {
      const parsed = parseInt(value, 10);
      return Number.isFinite(parsed) ? parsed : fallback;
    };

    const getSlideDuration = () => getDuration(root.dataset.slideDuration, 320);
    const getGrowDuration = () => getDuration(root.dataset.growDuration, 320);
    const getFadeDuration = () => getDuration(root.dataset.fadeDuration, 180);

    const finishCloseDrawer = () => {
      root.classList.remove('is-open');
      root.classList.remove('is-closing');
      collapseMinimalQtyControls();
      unlockPage();
      closeTimer = null;
    };

    const scheduleCloseDrawer = (duration) => {
      if (closeTimer) {
        window.clearTimeout(closeTimer);
      }
      root.classList.add('is-closing');
      closeTimer = window.setTimeout(() => {
        finishCloseDrawer();
      }, duration);
    };

    const lockPage = () => {
      const scrollY = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
      document.documentElement.classList.add('kozler-cart-locked');
      document.body.classList.add('kozler-cart-locked');
      document.body.style.top = `-${scrollY}px`;
      root.dataset.scrollY = String(scrollY);
    };

    const unlockPage = () => {
      document.documentElement.classList.remove('kozler-cart-locked');
      document.body.classList.remove('kozler-cart-locked');
      document.documentElement.classList.remove('js-drawer-open');
      document.body.style.top = '';
      const scrollY = parseInt(root.dataset.scrollY || '0', 10) || 0;
      window.scrollTo(0, scrollY);
      root.dataset.scrollY = '';
    };

    const openDrawer = () => {
      if (closeTimer) {
        window.clearTimeout(closeTimer);
        closeTimer = null;
      }
      root.classList.remove('is-closing');
      collapseMinimalQtyControls();
      lockPage();
      root.classList.add('is-open');
    };

    const closeDrawer = () => {
      const isOpen = root.classList.contains('is-open');
      const isClosing = root.classList.contains('is-closing');
      const isLocked = document.documentElement.classList.contains('kozler-cart-locked');
      if (isClosing) return;
      if (!isOpen && !isLocked) return;
      if (!isOpen) {
        unlockPage();
        return;
      }

      if (isGrowStyle()) {
        scheduleCloseDrawer(getFadeDuration() + getGrowDuration());
        return;
      }

      root.classList.remove('is-open');
      scheduleCloseDrawer(getSlideDuration());
    };

    const openAndRefresh = () => {
      openDrawer();
      return refreshDrawer();
    };

    const updateLegacyCartBubble = (sections = {}) => {
      const cartBubble = document.getElementById('cart-icon-bubble');
      const cartBubbleMarkup = sections['cart-icon-bubble'];
      if (!cartBubble || !cartBubbleMarkup) return;

      const parsed = new DOMParser().parseFromString(cartBubbleMarkup, 'text/html');
      const replacement = parsed.querySelector('.shopify-section');
      if (replacement) {
        cartBubble.innerHTML = replacement.innerHTML;
      }
    };

    let activeElement = null;
    const cartBridge = {
      classList: {
        contains: (className) => {
          if (className === 'is-empty') {
            return normalizeCount(root.getAttribute('data-cart-count')) === 0;
          }
          return root.classList.contains(className);
        },
        remove: (className) => {
          if (className === 'is-empty') return;
          root.classList.remove(className);
        },
      },
      close: () => closeDrawer(),
      getSectionsToRender: () =>
        document.getElementById('cart-icon-bubble')
          ? [
              {
                id: 'cart-icon-bubble',
              },
            ]
          : [],
      open: (triggeredBy) => {
        if (triggeredBy) {
          activeElement = triggeredBy;
        }
        openDrawer();
      },
      openAndRefresh: () => openAndRefresh(),
      refresh: () => refreshDrawer(),
      renderContents: (parsedState = {}) => {
        updateLegacyCartBubble(parsedState.sections || {});
        openDrawer();
        return Promise.resolve();
      },
      root,
      setActiveElement: (element) => {
        activeElement = element || null;
      },
      get activeElement() {
        return activeElement;
      },
    };

    window.KozlerCartDrawer = window.KozlerCartDrawer || {};
    window.KozlerCartBridge = cartBridge;
    window.KozlerCartDrawer.bridge = cartBridge;
    window.KozlerCartDrawer.open = openDrawer;
    window.KozlerCartDrawer.close = closeDrawer;
    window.KozlerCartDrawer.refresh = refreshDrawer;
    window.KozlerCartDrawer.openAndRefresh = openAndRefresh;

    window.theme = window.theme || {};
    window.theme.cart = window.theme.cart || {};
    window.theme.cart.refresh = refreshDrawer;
    window.theme.cart.open = openAndRefresh;
    window.theme.cart.close = closeDrawer;

    const REBUY_SMART_CART_SELECTOR =
      '#rebuy-cart, .rebuy-cart, [data-rebuy-cart], .rebuy-smart-cart, [data-rebuy-smart-cart]';

    const clearRebuyDocumentState = () => {
      ['rebuy-cart-open', 'rebuy-smart-cart-open', 'smart-cart--enabled', 'smart-cart--open'].forEach((className) => {
        document.documentElement.classList.remove(className);
        document.body.classList.remove(className);
      });
    };

    const suppressRebuyDrawer = () => {
      const rebuyDrawers = document.querySelectorAll(REBUY_SMART_CART_SELECTOR);
      rebuyDrawers.forEach((drawerEl) => {
        drawerEl.setAttribute('aria-hidden', 'true');
        drawerEl.setAttribute('hidden', 'hidden');
        drawerEl.classList.remove('is-visible');
        drawerEl.classList.remove('smart-cart--visible');
        drawerEl.style.setProperty('display', 'none', 'important');
        drawerEl.style.setProperty('visibility', 'hidden', 'important');
        drawerEl.style.setProperty('opacity', '0', 'important');
        drawerEl.style.setProperty('pointer-events', 'none', 'important');
      });
      clearRebuyDocumentState();
    };

    const destroyRebuySmartCart = () => {
      const smartCart = window.Rebuy && window.Rebuy.SmartCart;
      if (!smartCart) {
        suppressRebuyDrawer();
        return;
      }

      try {
        if (typeof smartCart.destroy === 'function') {
          smartCart.destroy();
        }
      } catch (error) {
        /* noop */
      }

      try {
        smartCart.skip_open = true;
      } catch (error) {
        /* noop */
      }

      try {
        smartCart.visible = false;
      } catch (error) {
        /* noop */
      }

      try {
        smartCart.status = 'disabled';
      } catch (error) {
        /* noop */
      }

      suppressRebuyDrawer();
    };

    const ensureRebuySuppressionStyle = () => {
      if (document.getElementById('kozler-rebuy-suppress-style')) return;
      const styleEl = document.createElement('style');
      styleEl.id = 'kozler-rebuy-suppress-style';
      styleEl.textContent = `
        #rebuy-cart,
        .rebuy-cart,
        [data-rebuy-cart],
        .rebuy-smart-cart,
        [data-rebuy-smart-cart] {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
          pointer-events: none !important;
        }
      `;
      document.head.appendChild(styleEl);
    };

    const patchRebuyBridge = () => {
      window.Rebuy = window.Rebuy || {};
      window.Rebuy.Cart = window.Rebuy.Cart || {};
      window.Rebuy.SmartCart = window.Rebuy.SmartCart || {};
      const openKozlerCartFromRebuy = () => {
        destroyRebuySmartCart();
        return openAndRefresh();
      };

      try {
        window.Rebuy.SmartCart.skip_open = true;
      } catch (error) {
        /* noop */
      }

      window.Rebuy.Cart.show = openKozlerCartFromRebuy;
      window.Rebuy.Cart.open = openKozlerCartFromRebuy;
      window.Rebuy.Cart.hide = () => {
        destroyRebuySmartCart();
        closeDrawer();
      };
      window.Rebuy.Cart.close = window.Rebuy.Cart.hide;
      window.Rebuy.SmartCart.show = openKozlerCartFromRebuy;
      window.Rebuy.SmartCart.open = openKozlerCartFromRebuy;
      window.Rebuy.SmartCart.init = () => {
        destroyRebuySmartCart();
        return window.Rebuy.SmartCart;
      };
      window.Rebuy.SmartCart.hide = () => {
        destroyRebuySmartCart();
        closeDrawer();
      };
      window.Rebuy.SmartCart.close = window.Rebuy.SmartCart.hide;
      window.Rebuy.SmartCart.toggle = () => {
        if (root.classList.contains('is-open')) {
          closeDrawer();
          return;
        }
        return openKozlerCartFromRebuy();
      };
    };

    const observeRebuyDrawer = () => {
      if (window.__kozlerRebuyObserverInstalled || typeof MutationObserver === 'undefined') return;

      const mutationTouchesRebuyDrawer = (mutation) =>
        Array.from(mutation.addedNodes || []).some(
          (node) => node instanceof Element && (node.matches(REBUY_SMART_CART_SELECTOR) || node.querySelector(REBUY_SMART_CART_SELECTOR))
        );

      const observer = new MutationObserver((mutations) => {
        if (!mutations.some(mutationTouchesRebuyDrawer)) return;
        patchRebuyBridge();
        destroyRebuySmartCart();
      });

      observer.observe(document.documentElement, { childList: true, subtree: true });
      window.__kozlerRebuyObserverInstalled = true;
    };

    ensureRebuySuppressionStyle();
    patchRebuyBridge();
    destroyRebuySmartCart();
    observeRebuyDrawer();
    let rebuyPatchAttempts = 0;
    const rebuyPatchTimer = window.setInterval(() => {
      rebuyPatchAttempts += 1;
      patchRebuyBridge();
      destroyRebuySmartCart();
      if (rebuyPatchAttempts >= 20) {
        window.clearInterval(rebuyPatchTimer);
      }
    }, 250);
    document.addEventListener('rebuy:smartcart.init', () => {
      patchRebuyBridge();
      destroyRebuySmartCart();
    });
    document.addEventListener('rebuy:smartcart.ready', () => {
      patchRebuyBridge();
      destroyRebuySmartCart();
    });
    document.addEventListener('rebuy:smartcart.show', () => {
      patchRebuyBridge();
      destroyRebuySmartCart();
      openAndRefresh();
    });
    document.addEventListener('rebuy:smartcart.hide', () => {
      patchRebuyBridge();
      destroyRebuySmartCart();
    });

    const patchModalPortal = () => {
      const modalPortal = window.modalPortal;
      if (!modalPortal || typeof modalPortal.openModalById !== 'function') return;
      if (modalPortal.__kozlerCartPatched) return;

      const originalOpenModalById = modalPortal.openModalById.bind(modalPortal);
      modalPortal.openModalById = (id, ...args) => {
        if (id === 'CartDrawer') {
          openAndRefresh();
          return;
        }
        return originalOpenModalById(id, ...args);
      };
      modalPortal.__kozlerCartPatched = true;
    };

    patchModalPortal();
    document.addEventListener('DOMContentLoaded', patchModalPortal, { once: true });
    setTimeout(patchModalPortal, 500);

    const getPathname = (urlString) => {
      try {
        return new URL(urlString, window.location.origin).pathname.replace(/\/+$/, '');
      } catch (error) {
        return '';
      }
    };

    const isCartAddPath = (pathname) => /\/cart\/add(?:\.js)?$/.test(pathname || '');
    const isCartMutationPath = (pathname) => /\/cart\/(?:change|update|clear)(?:\.js)?$/.test(pathname || '');

    let queuedCartRefreshTimer = null;
    let queuedCartOpen = false;
    const queueCartRefresh = (shouldOpen) => {
      if (shouldOpen) {
        queuedCartOpen = true;
      }
      if (queuedCartRefreshTimer) return;

      queuedCartRefreshTimer = window.setTimeout(() => {
        const shouldOpenDrawer = queuedCartOpen;
        queuedCartOpen = false;
        queuedCartRefreshTimer = null;
        if (shouldOpenDrawer) {
          openDrawer();
        }
        refreshDrawer();
      }, 40);
    };

    if (!window.__kozlerCartFetchHookInstalled) {
      window.__kozlerCartFetchHookInstalled = true;
      const nativeFetch = window.fetch.bind(window);
      const hasHeader = (headers, headerName) => {
        if (!headers) return false;
        if (typeof Headers !== 'undefined' && headers instanceof Headers) {
          return headers.has(headerName);
        }
        if (Array.isArray(headers)) {
          return headers.some(([key]) => String(key || '').toLowerCase() === headerName.toLowerCase());
        }
        if (typeof headers === 'object') {
          return Object.keys(headers).some((key) => key.toLowerCase() === headerName.toLowerCase());
        }
        return false;
      };
      const getHeaderValue = (headers, headerName) => {
        if (!headers) return '';
        if (typeof Headers !== 'undefined' && headers instanceof Headers) {
          return headers.get(headerName) || '';
        }
        if (Array.isArray(headers)) {
          const found = headers.find(([key]) => String(key || '').toLowerCase() === headerName.toLowerCase());
          return found ? String(found[1] || '') : '';
        }
        if (typeof headers === 'object') {
          const key = Object.keys(headers).find((name) => name.toLowerCase() === headerName.toLowerCase());
          return key ? String(headers[key] || '') : '';
        }
        return '';
      };
      const getManualCartMode = (requestArgs) => {
        const [resource, options] = requestArgs;
        if (typeof Request !== 'undefined' && resource instanceof Request) {
          return resource.headers.get(KOZLER_CART_MANUAL_HEADER) || '';
        }
        if (!hasHeader(options && options.headers, KOZLER_CART_MANUAL_HEADER)) return '';
        return getHeaderValue(options && options.headers, KOZLER_CART_MANUAL_HEADER) || 'true';
      };

      window.fetch = (...args) => {
        const manualCartMode = getManualCartMode(args);
        const responsePromise = nativeFetch(...args);
        responsePromise
          .then((response) => {
            if (!response || !response.ok || !response.url) return;
            const pathname = getPathname(response.url);
            if (manualCartMode === 'drawer' && (isCartAddPath(pathname) || isCartMutationPath(pathname))) {
              return;
            }
            if (isCartAddPath(pathname)) {
              if (manualCartMode) {
                queueCartRefresh(false);
                return;
              }
              queueCartRefresh(true);
              return;
            }
            if (isCartMutationPath(pathname)) {
              queueCartRefresh(false);
            }
          })
          .catch(() => {});
        return responsePromise;
      };
    }

    if (!window.__kozlerCartXhrHookInstalled && typeof window.XMLHttpRequest !== 'undefined') {
      window.__kozlerCartXhrHookInstalled = true;
      const nativeXhrOpen = window.XMLHttpRequest.prototype.open;
      const nativeXhrSend = window.XMLHttpRequest.prototype.send;

      window.XMLHttpRequest.prototype.open = function patchedOpen(method, url, ...rest) {
        this.__kozlerCartPathname = getPathname(url || '');
        return nativeXhrOpen.call(this, method, url, ...rest);
      };

      window.XMLHttpRequest.prototype.send = function patchedSend(...args) {
        if (!this.__kozlerCartListenerAttached) {
          this.__kozlerCartListenerAttached = true;
          this.addEventListener('loadend', () => {
            const status = Number(this.status) || 0;
            if (status < 200 || status >= 300) return;
            const pathname = this.__kozlerCartPathname || getPathname(this.responseURL || '');
            if (isCartAddPath(pathname)) {
              queueCartRefresh(true);
              return;
            }
            if (isCartMutationPath(pathname)) {
              queueCartRefresh(false);
            }
          });
        }
        return nativeXhrSend.apply(this, args);
      };
    }

    document.addEventListener('rebuy:cart.add', () => queueCartRefresh(true));
    document.addEventListener('rebuy:cart.change', () => {
      destroyRebuySmartCart();
      queueCartRefresh(false);
    });
    document.addEventListener('rebuy:cart:open', () => {
      destroyRebuySmartCart();
      openAndRefresh();
    });
    document.addEventListener('rebuy:cart:show', () => {
      destroyRebuySmartCart();
      openAndRefresh();
    });
    document.addEventListener('cart:updated', (event) => {
      if (event?.detail?.source === 'kozler-cart-drawer') return;
      refreshDrawer();
    });
    document.addEventListener('cart:change', (event) => {
      if (event?.detail?.source === 'kozler-cart-drawer') return;
      refreshDrawer();
    });
    document.addEventListener('cart:refresh', (event) => {
      if (event?.detail?.source === 'kozler-cart-drawer') return;
      refreshDrawer();
    });

    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:section:select', (event) => {
        if (event.detail && event.detail.sectionId === sectionId) {
          openDrawer();
        }
      });

      document.addEventListener('shopify:section:deselect', (event) => {
        if (event.detail && event.detail.sectionId === sectionId) {
          closeDrawer();
        }
      });
    }

    document.addEventListener('click', (event) => {
      const openOnSubmitTrigger = event.target.closest(`[${OPEN_ON_SUBMIT_ATTR}]`);
      if (openOnSubmitTrigger) {
        lastOpenOnSubmit = openOnSubmitTrigger;
        lastOpenOnSubmitAt = Date.now();
      } else {
        lastOpenOnSubmit = null;
        lastOpenOnSubmitAt = 0;
      }

      const opener = event.target.closest(
        '[data-kozler-cart-open], [aria-controls="CartDrawer"], [data-cart-open], [data-cart-toggle], [data-cart-icon]'
      );
      const closer = event.target.closest('[data-kozler-cart-close], [data-cart-dismiss]');
      const overlay = event.target.closest('.kozler-cart__overlay');

      if (opener) {
        event.preventDefault();
        openAndRefresh();
        return;
      }

      if (closer || (overlay && root.dataset.overlayEnabled !== 'false')) {
        event.preventDefault();
        closeDrawer();
        return;
      }
    }, true);

    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      const fallbackSubmitter = Date.now() - lastOpenOnSubmitAt < 2000 ? lastOpenOnSubmit : null;
      const submitter = event.submitter || fallbackSubmitter;
      if (!submitter || !submitter.hasAttribute(OPEN_ON_SUBMIT_ATTR)) return;
      if (submitter.form && submitter.form !== form) return;

      setTimeout(() => {
        if (event.defaultPrevented) return;
        openDrawer();
        refreshDrawer();
      }, 0);

      lastOpenOnSubmit = null;
      lastOpenOnSubmitAt = 0;
    });

    document.addEventListener('click', (event) => {
      if (event.target.closest('[data-qty-minimal]')) return;
      collapseMinimalQtyControls();
    });

    document.addEventListener('click', (event) => {
      const openTooltip = root.querySelector('[data-terms-tooltip].is-open');
      if (!openTooltip) return;
      if (event.target.closest('[data-terms-tooltip]')) return;
      const trigger = openTooltip.querySelector('[data-terms-tooltip-trigger]');
      openTooltip.classList.remove('is-open');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    });

    document.addEventListener('keyup', (event) => {
      if (event.key === 'Escape' && root.classList.contains('is-open')) {
        closeDrawer();
      }
    });

    updateTermsState();
  };

  const initAll = () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach((rootEl) => initKozlerCartDrawer(rootEl));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const loadedSectionId = event?.detail?.sectionId;
    if (!loadedSectionId) {
      initAll();
      return;
    }
    const rootEl = document.getElementById(`kozler-cart-${loadedSectionId}`);
    if (rootEl) initKozlerCartDrawer(rootEl);
  });
})();
